/**
 * ThermostatComponent
 **/

import React, { useCallback } from 'react';
import _ from 'lodash';
import './thermostat.css';

/* MUI */
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { Alert, Avatar } from '@mui/material';
import Button from '@mui/material/Button';

/* Icons */
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BoltIcon from '@mui/icons-material/Bolt';
import { ThermostatIcon } from './components/status-icons/thermostat-icon';

/* Sub Components */
import { LocalPermissionsPanel } from './components/local-permissions-panel';
import { LouvrePanel } from './components/louvre-panel';
import {
  DisplayScreen,
  MenuButtons,
  StatusIcons,
  SystemEdit,
  UpdatePending,
  PlmAbortDialog,
  PlmScheduleTimelineDialog,
} from './components';

/* Types */
import {
  CurrentAttributes,
  DayOfWeek,
  EmptyWeeklySchedule,
  DefaultSchedule,
  InputMaybe,
  MyDailySchedule,
  MyScheduleObject,
  MyScheduleSetting,
  MyThermostat,
  MyWeeklySchedule,
  ThermostatComponentProps,
  ThermostatUpdateFields,
  TimeOfDay,
  useThermostatDetailQuery,
  useThermostatDetailUpdateSubscription,
  useUpdateThermostatMutation,
  dailyScheduleDefault as _dailyScheduleDefault,
} from './types';

/* Lib Imports (component specific functions) */
import {
  updateInfoBox,
  formatHoldEnd,
  updateSubscribedValues,
  areSchedulesEqual,
} from './lib';

/* Util */
import { formatTemperatureString } from '../../util';
import {
  applySetpointLimitToWeeklyScheduleValues,
  validateScheduleLimitValues,
} from '../../util/apply-setpoint-limit-to-schedule-values';
import { nextAutoTarget } from './lib/auto-target';
import {
  nativeTargetCapabilitySignature,
  nativeTargetDigits,
  nativeTargetDisplayValue,
  nextNativeTarget,
} from './lib/native-target';
import {
  buildThermostatControlEdit,
  usesCoreCentidegreeTargets,
  getSharedTargetDefaults,
} from './lib/thermostat-control-edit';
import { usesObservationEvidence } from './lib/observation-freshness';
import {
  effectiveSnapshotPower,
  getMitsubishiTargetEditBlockReason,
} from './lib/mitsubishi-power-edit';

import { Notifier } from '../../../../system/services/notificationManager';
import {
  Celsius,
  Fahrenheit,
} from '../../../../system/models/temperatureUnits';

import { DeviceInfo as DeviceInfoBox } from '../../shared-ui/device-info';
import {
  DeviceAlerts,
  ToggleAlertDetailsButton,
} from '../../shared-ui/alerts-info';

import { updateCacheFromSubscriptionEvent } from '../../../../../helpers/subscriptionUtils';
import {
  deviceDefaultLimits,
  resolveDeviceLimitDomains,
  limitCapabilitySignature,
  validateDisplayLimits,
  canEditDeviceLimits,
} from './lib/limit-domains';
import {
  LIMIT_FIELDS,
  LimitField,
  admissibleDomainEnds,
} from '../../../../../common/thermostat-limits';
import { defaultAbsoluteLimits } from '../../../helpers';
import { convertLimitTemperature } from '../../util/convert-temp-units';

import { BLANK } from '../../shared-ui/constants';
import {
  EnumScheduleType,
  Property,
  TemperatureUnit,
  Unit,
  UpdateThermostatMutationVariables,
  useAbortPlmCurtailmentTaskMutation,
  useGetPlmCurtailmentTasksForDeviceQuery,
  usePlmEventResponseByIdQuery,
} from '../../../../../types/generated-types';
// import { SmartPowerOutletLink } from './types/smart-power-outlet-link';
import { SmartPowerOutletLink } from './components/status-icons/smart-power-outlet-link';
import { ThermostatSchedule } from './components/schedule/thermostat-schedule';
import { ThermostatScheduleUpdateDialog } from './components/dialogs/thermostat-schedule-update-dialog';
import { useAuthenticator } from '../../../../auth/AuthenticationContext';
import {
  convertToPreferredDailyScheduleUnits,
  convertToPreferredWeeklyScheduleUnits,
} from '../../util/convert-to-preferred-schedule-units';
import {
  useDeviceHasAlerts,
  useDeviceAndSourceAreOnline,
  useSourceOfflineTimestamp,
  useDeviceOfflineTimestamp,
} from '../../../../system/AlertsManager';
import {
  BottomMenuItems,
  useInjectableComponents,
} from '../../../../system/services/injectableComponentsManager';
import { useAuthorizer } from '../../../../auth/AuthorizationContext';
import { useClearThermostatUpdateStatusMutation } from '../../../../../types/generated-types';
import RefreshIcon from '@mui/icons-material/Refresh';
import { shouldLoadPlmDeviceSchedule } from './plmScheduleLoading';
import {
  PendingUpdateAction,
  resolvePendingUpdateState,
  ViewMode,
} from './pendingUpdateState';

export { ViewMode } from './pendingUpdateState';

export enum MenuAction {
  EDIT_SCHEDULE = 'edit_schedule',
  EDIT_VALUES = 'edit_values',
  SAVE_CHANGES = 'save_changes',
  REVERT_CHANGES = 'revert_changes',
  CANCEL = 'cancel',
}

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return (
    <div
      style={{
        borderLeft: '1px solid rgba(0, 0, 0, 0.23)',
        height: '100%',
        display: 'flex',
        padding: expand ? '0px 0px 0px 0px' : '0px 0px 0px 5px',
        margin: expand ? '0px 12px 0px 0px' : '0px 6px 0px 0px',
      }}
    >
      <IconButton
        style={{
          padding: expand ? '0px 6px 0px 3px' : '0px 9px 0px 1px',
          margin: '0px 0px 0px 4px',
        }}
        {...other}
      />
    </div>
  );
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export function ThermostatComponent(props: ThermostatComponentProps) {
  const {
    id,
    pairedSensor,
    property,
    unit,
    smartPowerOutlet,
    preferredUnits = Fahrenheit,
  } = props;

  const propertySetpointProfile =
    unit?.property?.setpointLimitProfile ?? property?.setpointLimitProfile;
  const unitSetpointProfile = unit?.setpointLimitProfile;
  const limitProfileAssignments = React.useMemo(
    () => ({
      unitProfileId: unit?.setpointProfileId,
      propertyProfileId:
        unit?.property?.setpointProfileId ?? property?.setpointProfileId,
    }),
    [
      unit?.setpointProfileId,
      unit?.property?.setpointProfileId,
      property?.setpointProfileId,
    ],
  );

  const {
    data,
    loading,
    // TODO: Loading_error: should we handle error here for this query?
    // error,
  } = useThermostatDetailQuery({
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [updateThermostat] = useUpdateThermostatMutation({
    refetchQueries: ['ThermostatDetail'],
  });

  useThermostatDetailUpdateSubscription({
    variables: { ids: [id] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  /* State */
  const [device, setDevice] = React.useState<MyThermostat>();
  const [, setObservationTick] = React.useState(0);
  const observationNow = Date.now();
  const [statusMessage, setStatusMessage] = React.useState(BLANK);
  const [originalValues, setOriginalValues] =
    React.useState<CurrentAttributes>();
  const [newValues, setNewValues] = React.useState<CurrentAttributes>();
  const editContext = React.useRef<string>();
  // Reported values can change while editing. Diff against the values the user
  // started with so telemetry never turns an untouched control into a write.
  const editBaseline = React.useRef<CurrentAttributes>();
  const [editInvalidated, setEditInvalidated] = React.useState(false);
  const [limitDraftInvalid, setLimitDraftInvalid] = React.useState(false);
  const pendingValueSave = React.useRef<{
    context: string;
    invalidated: boolean;
  }>();
  // schedule: original schedule pulled from the device and convert to preferred units
  const [schedule, setSchedule] = React.useState<MyScheduleObject>(
    _.cloneDeep(DefaultSchedule),
  );
  // newSchedule: new schedule set by the user or default/placeholder schedule
  const [newSchedule, setNewSchedule] = React.useState<MyScheduleObject>(
    _.cloneDeep(DefaultSchedule),
  );
  const [scheduleType, setScheduleType] = React.useState<EnumScheduleType>();
  const [viewMode, setViewMode] = React.useState(ViewMode.NORMAL);
  const recoveringFromFailedUpdate = React.useRef(false);
  const failedUpdateData = React.useRef<unknown>();
  const latestQueryData = React.useRef<unknown>();
  // Ignore late HTTP responses once the user has started a newer edit. This
  // does not determine pending/completion; metric subscriptions do that.
  const updateAttempt = React.useRef(0);
  React.useEffect(
    () => () => {
      updateAttempt.current += 1;
    },
    [id],
  );
  const [expanded, setExpanded] = React.useState(false);
  const [alertsExpanded, setAlertsExpanded] = React.useState(false);
  // const [isOffline, setIsOffline] = React.useState(false);
  const { user } = useAuthenticator();
  const { can } = useAuthorizer();
  const canAccessPlm = can('be', 'super');
  const [updateThermostatVariables, setUpdateThermostatVariables] =
    React.useState<UpdateThermostatMutationVariables | undefined>(undefined);
  const [
    openThermostatScheduleUpdateDialog,
    setOpenThermostatScheduleUpdateDialog,
  ] = React.useState(false);

  const [isPlmActive, setPlmActive] = React.useState(false);
  const [plmAbortDialogOpen, setPlmAbortDialogOpen] = React.useState(false);
  const [plmScheduleDialogOpen, setPlmScheduleDialogOpen] =
    React.useState(false);
  const [plmScheduleTasks, setPlmScheduleTasks] = React.useState<any[]>([]);

  // PLM abort mutation hook
  const [abortPlmCurtailmentTask, { loading: abortLoading }] =
    useAbortPlmCurtailmentTaskMutation();

  // PLM schedule query hook
  const shouldLoadPlmSchedule = shouldLoadPlmDeviceSchedule({
    canAccessPlm,
    deviceId: device?.deviceId,
    isPlmActive,
    isAbortDialogOpen: plmAbortDialogOpen,
    isScheduleDialogOpen: plmScheduleDialogOpen,
  });

  const { data: plmScheduleData } = useGetPlmCurtailmentTasksForDeviceQuery({
    variables: { deviceId: device?.deviceId || '' },
    skip: !shouldLoadPlmSchedule,
  });

  // Get event response ID from first task to fetch event response details
  const eventResponseId =
    plmScheduleData?.plmCurtailmentTasksForDevice?.[0]?.plmEventResponseId;

  // PLM event response query hook
  const { data: plmEventResponseData } = usePlmEventResponseByIdQuery({
    variables: { _id: eventResponseId || '' },
    skip: !eventResponseId || !canAccessPlm,
  });

  // Update PLM schedule tasks when data changes
  React.useEffect(() => {
    if (!shouldLoadPlmSchedule) {
      setPlmScheduleTasks([]);
    } else if (plmScheduleData?.plmCurtailmentTasksForDevice) {
      setPlmScheduleTasks(plmScheduleData.plmCurtailmentTasksForDevice);
    }
  }, [shouldLoadPlmSchedule, plmScheduleData]);

  const applySetpointLimits = useCallback(
    (
      settings: CurrentAttributes,
      usesCustomLimits?: boolean | null,
      device?: MyThermostat,
    ): CurrentAttributes => {
      const defaults = deviceDefaultLimits(device);
      const newSettings = { ...settings };
      for (const field of LIMIT_FIELDS) {
        const base =
          `base${field[0].toUpperCase()}${field.slice(1)}` as keyof CurrentAttributes;
        newSettings[field] = usesCustomLimits
          ? convertLimitTemperature(
              device?.[field]?.value ?? defaults[field],
              preferredUnits,
            )
          : (settings[base] as number | undefined);
      }
      return newSettings as CurrentAttributes;
    },
    [preferredUnits],
  );

  // Compare confirmed command semantics, not ordinary reading values. Drafts
  // must never be reinterpreted when a subscription changes the control shape.
  const confirmedDevice = data?.thermostatById as MyThermostat | undefined;
  latestQueryData.current = data;
  const powerSnapshot = confirmedDevice?.separatePowerControlV1;
  const louvreSnapshot = powerSnapshot ?? confirmedDevice?.louvreControlV1;
  const permissionsSnapshot = confirmedDevice?.localControlPermissionsV1;
  const separatePower = !!powerSnapshot;
  const confirmedPower = powerSnapshot
    ? effectiveSnapshotPower(powerSnapshot)
    : undefined;
  const powerPending = confirmedDevice?.hasPendingUpdates === true;
  const powerTargetBlockReason = powerSnapshot
    ? getMitsubishiTargetEditBlockReason(
        powerSnapshot,
        viewMode === ViewMode.UPDATING ||
          viewMode === ViewMode.AWAITING ||
          powerPending,
        newValues ?? originalValues,
      )
    : null;
  const canWriteSetpoint = (
    metric:
      | 'heatSetpoint'
      | 'coolSetpoint'
      | 'setpoint'
      | 'operatingMode'
      | 'fanMode'
      | 'airDirection'
      | 'fanSpeed',
  ) =>
    (metric !== 'setpoint' &&
      ['Core', 'HMM'].includes(confirmedDevice?.source?.type ?? '')) ||
    confirmedDevice?.deviceMetrics?.some(
      (entry) => entry?.key === metric && entry.value?.readOnly === false,
    ) === true;
  const confirmedValues = confirmedDevice
    ? applySetpointLimits(
        updateSubscribedValues(
          confirmedDevice,
          preferredUnits,
          unitSetpointProfile,
          propertySetpointProfile,
          limitProfileAssignments,
        ),
        confirmedDevice.useCustomSetpointLimits,
        confirmedDevice,
      )
    : undefined;
  const confirmedContext = JSON.stringify({
    id,
    preferredUnits,
    limitCapabilities: limitCapabilitySignature(confirmedDevice),
    targetCapabilities: nativeTargetCapabilitySignature(confirmedDevice),
    limitProfileAssignments,
    canUpdate: can('update', 'Thermostat'),
    sourceType: confirmedDevice?.source?.type,
    supportsScheduling: confirmedDevice?.supportsScheduling,
    separatePower: powerSnapshot?.controlContract,
    allowedOperatingModes: powerSnapshot?.allowedOperatingModes,
    allowedFanSpeeds: powerSnapshot?.allowedFanSpeeds,
    supportsAirDirection: powerSnapshot?.supportsAirDirection,
    allowedAirDirections: powerSnapshot?.allowedAirDirections,
    reportedPowerMode: powerSnapshot?.powerMode?.value,
    powerPending: powerSnapshot ? !!powerPending : undefined,
    powerTargetBlockReason: powerSnapshot
      ? getMitsubishiTargetEditBlockReason(powerSnapshot, false)
      : null,
    powerPermissions: powerSnapshot
      ? [
          powerSnapshot.canEditPowerMode,
          powerSnapshot.canEditOperatingMode,
          powerSnapshot.canEditTargets,
          powerSnapshot.canEditFanSpeed,
          powerSnapshot.canEditAirDirection,
        ]
      : undefined,
    values: _.pick(confirmedValues, [
      'operatingMode',
      'systemType',
      'supportsAutoMode',
      'supportsSingleSetpointAutoMode',
      'supportsDualSetpointAutoMode',
      'supportsSystemType',
      'supportsFanOperations',
      'supportsShowFanButton',
      'isMultiSpeedThermostat',
      'modbusRTU',
      'keypadLockoutLevels',
      'minSetpoint',
      'maxSetpoint',
      'useCustomSetpointLimits',
      'baseMinHeatSetpointLimit',
      'baseMaxHeatSetpointLimit',
      'baseMinCoolSetpointLimit',
      'baseMaxCoolSetpointLimit',
      'minHeatSetpointLimit',
      'maxHeatSetpointLimit',
      'minCoolSetpointLimit',
      'maxCoolSetpointLimit',
    ]),
    metricPermissions: confirmedDevice?.deviceMetrics
      ?.filter(
        (metric) =>
          !confirmedDevice.isMitsubishiThermostat ||
          metric?.key !== 'localControlPermissions',
      )
      ?.map((metric) => [metric?.key, metric?.value?.readOnly])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  });
  const latestConfirmedContext = React.useRef(confirmedContext);
  latestConfirmedContext.current = confirmedContext;
  const isEditingValues =
    viewMode === ViewMode.EDIT || viewMode === ViewMode.EDIT_UNCHANGED;
  // Offline transitions can leave an unsaved draft behind. Outside editing
  // or a submitted update, the separate-power display uses reported values.
  const displayValues =
    separatePower &&
    !isEditingValues &&
    viewMode !== ViewMode.UPDATING &&
    viewMode !== ViewMode.AWAITING
      ? originalValues
      : (newValues ?? originalValues);
  // Off commands omit targets, so their pending captions use reported targets.
  const targetDisplayValues =
    separatePower && !isEditingValues && displayValues?.powerMode === 'Off'
      ? originalValues
      : displayValues;
  const requiresEditReload =
    isEditingValues &&
    (editInvalidated ||
      (editContext.current !== undefined &&
        editContext.current !== confirmedContext));
  React.useEffect(() => {
    if (requiresEditReload) setEditInvalidated(true);
  }, [requiresEditReload]);
  React.useEffect(() => {
    if (
      pendingValueSave.current &&
      pendingValueSave.current.context !== confirmedContext
    ) {
      pendingValueSave.current.invalidated = true;
    }
  }, [confirmedContext]);

  const reloadConfirmedValues = () => {
    editContext.current = confirmedContext;
    editBaseline.current = confirmedValues ? { ...confirmedValues } : undefined;
    setEditInvalidated(false);
    setLimitDraftInvalid(false);
    setOriginalValues(confirmedValues);
    setNewValues(can('update', 'Thermostat') ? confirmedValues : undefined);
    setViewMode(
      can('update', 'Thermostat') ? ViewMode.EDIT_UNCHANGED : ViewMode.NORMAL,
    );
  };

  const handleUpdateStatusCleared = (
    clearedDeviceId: string,
    clearAttempt?: number,
  ) => {
    const currentDevice = (latestQueryData.current as typeof data)
      ?.thermostatById;
    if (
      clearAttempt !== updateAttempt.current ||
      currentDevice?.deviceId !== clearedDeviceId
    )
      return;

    // The server confirmed this administrative clear. Abandon the local edit
    // and ignore the cached pending snapshot until fresh query data arrives.
    updateAttempt.current += 1;
    pendingValueSave.current = undefined;
    recoveringFromFailedUpdate.current = true;
    failedUpdateData.current = latestQueryData.current;
    setNewValues(undefined);
    setViewMode(ViewMode.NORMAL);
  };

  /* Effects */

  /**
   * Respond to changes of query & subscription 'data', as well as component parameters
   * of 'preferredUnits', 'propertySetpointProfile', 'unitSetpointProfile'.
   *
   * Initialize or Update Device Readings
   * - Create a map of current values that can be edited within this interface.
   * - Apply unit or property level setpoint limit profiles.
   * - Apply preferred temperature units to temperature data.
   * - Initialize schedule information.
   * - Determine the viewMode for the interface based on updates from query data.
   *
   * Initialize or Update Schedule
   * - create a schedule object matching the structure of the incoming schedule data
   * - use a new object and initialize set temperatures using the preferred unit of measure
   * - store the schedule object in local state
   *
   **/
  React.useEffect(() => {
    const myDevice = data && (data.thermostatById as MyThermostat);

    if (myDevice) {
      setDevice(myDevice);
      setPlmActive(canAccessPlm && (myDevice.meta?.plmActive ?? false));
      setSchedule((current) => {
        const scheduleValue: MyScheduleObject['value'] =
          _.cloneDeep(EmptyWeeklySchedule);
        const { schedule } = myDevice;

        Object.entries(schedule?.value || {}).forEach(
          ([dayOfWeek, dailySchedule]) => {
            const _dayOfWeek = dayOfWeek as unknown as DayOfWeek;
            const _dailySchedule = dailySchedule as unknown as MyDailySchedule;
            scheduleValue[_dayOfWeek] = {};
            Object.entries(_dailySchedule).forEach(
              ([timeOfDay, scheduleSetting]: [string, MyScheduleSetting]) => {
                const _timeOfDay = timeOfDay as unknown as TimeOfDay;
                scheduleValue[_dayOfWeek][_timeOfDay] = {};

                scheduleValue[_dayOfWeek][_timeOfDay].minimum =
                  convertLimitTemperature(
                    scheduleSetting.minimum,
                    preferredUnits,
                  );

                scheduleValue[_dayOfWeek][_timeOfDay].maximum =
                  convertLimitTemperature(
                    scheduleSetting.maximum,
                    preferredUnits,
                  );
              },
            );
          },
        );

        const initialSchedule: MyScheduleObject = {
          id: schedule?.id as string,
          units: schedule?.units as string,
          timestamp: schedule?.timestamp as number,
          value: scheduleValue,
          updatePending: schedule?.updatePending as boolean,
        };

        return { ...current, ...initialSchedule };
      });

      // Set schedule type when data first loads and the thermostat has a schedule set

      if (!scheduleType && myDevice.scheduleType) {
        setScheduleType(myDevice.scheduleType);
      } else {
        if (myDevice?.schedule) {
          if (
            _.isEqual(myDevice.schedule.value, EmptyWeeklySchedule) &&
            !scheduleType
          ) {
            setScheduleType(EnumScheduleType.None);
          }
        }
      }

      let initialValues = updateSubscribedValues(
        myDevice,
        preferredUnits,
        unitSetpointProfile,
        propertySetpointProfile,
        limitProfileAssignments,
      );

      initialValues = applySetpointLimits(
        initialValues,
        myDevice.useCustomSetpointLimits,
        myDevice,
      );

      // A render caused by error recovery is not a new subscription event.
      // Keep the recovery guard until data changes, so a delayed pending event
      // cannot be mistaken for an edit made by another user.
      if (
        recoveringFromFailedUpdate.current &&
        data === failedUpdateData.current
      ) {
        setOriginalValues(initialValues);
        return;
      }
      const pendingUpdateState = resolvePendingUpdateState({
        viewMode,
        hasPendingUpdates: myDevice.hasPendingUpdates === true,
        recoveringFromFailedUpdate: recoveringFromFailedUpdate.current,
      });
      recoveringFromFailedUpdate.current =
        pendingUpdateState.recoveringFromFailedUpdate;

      switch (pendingUpdateState.action) {
        case PendingUpdateAction.AWAIT_LOCAL_UPDATE:
          setViewMode(ViewMode.AWAITING);
          break;
        case PendingUpdateAction.AWAIT_EXTERNAL_UPDATE:
          Notifier.warn('Thermostat being updated on another device');
          setViewMode(ViewMode.AWAITING);
          setNewValues(undefined);
          break;
        case PendingUpdateAction.COMPLETE_UPDATE:
          setOriginalValues(initialValues);
          setNewValues(initialValues);
          const completedAttempt = updateAttempt.current;
          setTimeout(() => {
            if (completedAttempt !== updateAttempt.current) return;
            setNewValues(undefined);
            Notifier.info('Thermostat update completed');
            setViewMode(ViewMode.NORMAL);
          }, 1000);
          break;
        case PendingUpdateAction.IGNORE_STALE_PENDING:
          setOriginalValues(initialValues);
          break;
        case PendingUpdateAction.NONE:
          if (!myDevice.hasPendingUpdates) {
            setOriginalValues(initialValues);
          }
          break;
      }
    }
  }, [
    data,
    preferredUnits,
    propertySetpointProfile,
    unitSetpointProfile,
    limitProfileAssignments,
    viewMode,
    scheduleType,
    applySetpointLimits,
    canAccessPlm,
  ]);

  const sourceOfflineTimestamp = useSourceOfflineTimestamp(device);
  const deviceOfflineTimestamp = useDeviceOfflineTimestamp(device);
  const isOffline = !!(sourceOfflineTimestamp || deviceOfflineTimestamp);
  const hasAlerts = useDeviceHasAlerts(device);

  React.useEffect(() => {
    if (
      viewMode !== ViewMode.NORMAL &&
      originalValues !== undefined &&
      newValues === undefined
    ) {
      setNewValues({ ...originalValues });
    }
  }, [viewMode]);

  /**
   * Respond to changes in the 'isOffline' state flag
   */
  React.useEffect(() => {
    isOffline ? setViewMode(ViewMode.OFFLINE) : setViewMode(ViewMode.NORMAL);
  }, [isOffline]);

  /* Local display clock only: never participates in edit synchronization. */
  React.useEffect(() => {
    const timer = window.setInterval(
      () => setObservationTick((tick) => tick + 1),
      60000,
    );
    return () => window.clearInterval(timer);
  }, []);
  const deviceInfo = confirmedDevice
    ? updateInfoBox(props, confirmedDevice, newValues, observationNow)
    : undefined;

  function showResumeSchedule(): boolean {
    const holdForever = device?.isHoldForever;
    const onHold = device?.hasSchedule && device?.isOffSchedule;
    return Boolean((holdForever || onHold) && !isOffline);
  }

  function briefScheduleStatus(): string | undefined {
    if (showBriefScheduleStatus()) {
      if (device) {
        if (device.isHoldForever) {
          return 'Schedule is set to hold forever';
        }
        // In Override, show Hold End time
        return `Schedule hold ends ${formatHoldEnd(device)}`;
      } else {
        // if device is undefined return undefined.
        console.warn(
          'Attempting to display SCHEDULE HOLD END for an undefined device',
        );
        return 'No schedule: Unknown device';
      }
    }
  }

  function showBriefScheduleStatus(): boolean {
    if (device?.isHoldForever) {
      // Hold Forever situation, always show brief status
      return true;
    }
    // In Override situation, show brief status if holdExpiryTime current
    const holdExpiryTimeTimestamp = device?.holdExpiryTime?.timestamp || 0;
    const holdStateTimestamp = device?.holdState?.timestamp || 0;
    return holdExpiryTimeTimestamp > holdStateTimestamp;
  }

  const handleUseCustomSetpointLimitsChange = (
    useCustomSetpointLimits: boolean,
  ) => {
    let myValues = {
      ...newValues,
      useCustomSetpointLimits,
    } as CurrentAttributes;

    // If switching from custom setpoint limits to non-custom limits, applySetpointLimits will adjust
    // the limits based on the assigned unit or property setpoint limit profile, if one exists.
    // If switching from non-custom limits to custom limits, applySetpointLimits will set the limits
    // to the default absolute setpoint limits - the most permissive set of limits.
    myValues = applySetpointLimits(
      myValues,
      useCustomSetpointLimits,
      confirmedDevice,
    );

    // Update setpoints if they fall outside the range of the new setpoint limits
    const conformingChanges = conformToSetpointLimits(myValues);

    // Construct the set of all new changes, based on current device attributes
    let newChanges: Record<string, number | string | boolean> = {
      useCustomSetpointLimits,
    };
    // Restore the complete policy in the draft, including endpoints equal to
    // the confirmed reading. Save compares this draft with the edit baseline;
    // filtering here leaves a discarded custom edit hidden behind the base UI.
    for (const field of LIMIT_FIELDS) {
      if (myValues[field] !== undefined) newChanges[field] = myValues[field]!;
    }
    // Include any setpoint changes
    if (conformingChanges) {
      newChanges = {
        ...newChanges,
        ...conformingChanges,
      };
    }

    handleChange(newChanges);
    return newChanges;
  };

  const handleSetpointLimitChange = React.useCallback(
    (
      key: string,
      newValue: number,
    ): Record<string, number | string | boolean> => {
      if (newValues !== undefined) {
        const myKey = key as keyof CurrentAttributes;
        let direction = 0;
        let mode;
        let limit;

        switch (key) {
          case 'maxCoolSetpointLimit':
            mode = 'cool';
            limit = 'max';
            break;
          case 'minCoolSetpointLimit':
            mode = 'cool';
            limit = 'min';
            break;
          case 'maxHeatSetpointLimit':
            mode = 'heat';
            limit = 'max';
            break;
          case 'minHeatSetpointLimit':
            mode = 'heat';
            limit = 'min';
            break;
          default:
            console.log(
              '[thermostat-handleSetpointLimitChange] unknown key:',
              key,
            );
            return {};
        }

        if (newValues[myKey] === newValue) {
          return {};
        } else if (((newValues[myKey] as number) ?? 0) < newValue) {
          direction = 1;
        } else {
          direction = -1;
        }

        const changes = { [key]: newValue };
        const myValues = { ...newValues, ...changes };

        const conformingChanges = conformToSetpointLimits(
          myValues,
          mode,
          limit,
          direction,
        );

        if (conformingChanges !== null) {
          const newChanges = { ...changes, ...conformingChanges };

          handleChange(newChanges);
          return newChanges;
        } else {
          return {};
        }
      } else {
        return {};
      }
    },
    [newValues],
  );

  const conformToSetpointLimits = React.useCallback(
    (
      values?: CurrentAttributes,
      mode?: string,
      limit?: string,
      direction?: number,
      enforce?: boolean,
    ) => {
      if (!enforce && newValues === undefined) {
        return {};
      }

      const baseValues = values
        ? values
        : newValues
          ? newValues
          : ({} as CurrentAttributes);

      if (confirmedDevice?.isMitsubishiThermostat) {
        return validateDisplayLimits(
          baseValues,
          confirmedDevice,
          preferredUnits,
        ).length && limit
          ? null
          : {};
      }

      // Limits retain exact Celsius policy precision in either display unit.
      // Rounded display defaults can move an untouched endpoint (7 C is 44.6 F,
      // not 45 F), and explicit device domains may differ from legacy defaults.
      const domains = resolveDeviceLimitDomains(confirmedDevice);
      if (!domains.ok) return limit ? null : {};
      const conformanceBoundary = (field: LimitField, side: 'min' | 'max') => {
        const domain = domains.value[field];
        const ends = admissibleDomainEnds(domain);
        // Continuous open intervals have a comparison boundary but no selectable
        // endpoint. Keep conformance active; shared membership validation still
        // excludes that boundary. Discrete domains retain their grid endpoints.
        const boundary =
          side === 'min'
            ? (ends.minimum ?? domain.interval.lower)
            : (ends.maximum ?? domain.interval.upper);
        return convertLimitTemperature(boundary, preferredUnits)!;
      };
      const myDefaultAbsoluteLimits = {
        heat: {
          min: conformanceBoundary('minHeatSetpointLimit', 'min'),
          max: conformanceBoundary('maxHeatSetpointLimit', 'max'),
        },
        cool: {
          min: conformanceBoundary('minCoolSetpointLimit', 'min'),
          max: conformanceBoundary('maxCoolSetpointLimit', 'max'),
        },
      };
      const myDirection = direction ?? 1;
      const myMode = mode ?? 'heat';

      // The legacy conformance algorithm operates only on complete numeric controls.
      // Unknown targets or limits remain absent rather than becoming synthetic values.
      const required = [
        ...LIMIT_FIELDS,
        'heatSetpoint',
        'coolSetpoint',
      ] as const;
      if (required.some((key) => !Number.isFinite(baseValues[key])))
        return limit ? null : {};
      const myValues = { ...baseValues } as CurrentAttributes &
        Record<(typeof required)[number], number>;

      const isAutoMode = myValues.operatingMode === 'Auto';

      const deadband = preferredUnits === Celsius ? 1 : 2;

      // If Auto mode, could be converting from non-Auto mode, where the relationship
      // between heat and cool setpoints was ignored. So, need first of all to enforce
      // the deadband. Choosing to modify cool setpoint to correct for deadband.
      if (isAutoMode) {
        // check absolute limits
        if (
          myDefaultAbsoluteLimits.heat.min + deadband >
          myDefaultAbsoluteLimits.cool.max
        ) {
          Notifier.error(
            'Unable to honor deadband. Please contact Embue support.',
          );
          console.log(
            '[thermostat-conformToSetpointLimits] deadband impossible to honor',
          );
          return null;
        }

        if (mode === 'heat') {
          if (limit === 'max') {
            if (direction === 1) {
              if (
                myValues.maxHeatSetpointLimit > myDefaultAbsoluteLimits.heat.max
              ) {
                myValues.maxHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.max;
                // return null;
              }
            } else {
              if (
                myValues.maxHeatSetpointLimit < myDefaultAbsoluteLimits.heat.min
              ) {
                myValues.maxHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min;
                // return null;
              }
            }
          } else if (limit === 'min') {
            if (direction === 1) {
              if (
                myValues.minHeatSetpointLimit > myDefaultAbsoluteLimits.heat.max
              ) {
                myValues.minHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.max;
                // return null;
              }
            } else {
              if (
                myValues.minHeatSetpointLimit < myDefaultAbsoluteLimits.heat.min
              ) {
                myValues.minHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min;
                // return null;
              }
            }
          }
        } else if (mode === 'cool') {
          if (limit === 'max') {
            if (direction === 1) {
              if (
                myValues.maxCoolSetpointLimit > myDefaultAbsoluteLimits.cool.max
              ) {
                myValues.maxCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max;
                // return null;
              }
            } else {
              if (
                myValues.maxCoolSetpointLimit < myDefaultAbsoluteLimits.cool.min
              ) {
                myValues.maxCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.min;
                // return null;
              }
            }
          } else if (limit === 'min') {
            if (direction === 1) {
              if (
                myValues.minCoolSetpointLimit > myDefaultAbsoluteLimits.cool.max
              ) {
                myValues.minCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max;
                // return null;
              }
            } else {
              if (
                myValues.minCoolSetpointLimit < myDefaultAbsoluteLimits.cool.min
              ) {
                myValues.minCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.min;
                // return null;
              }
            }
          }
        }

        // Check absolute and internal relational values for heat limits.
        // min < a_min
        if (myValues.minHeatSetpointLimit < myDefaultAbsoluteLimits.heat.min) {
          myValues.minHeatSetpointLimit = myDefaultAbsoluteLimits.heat.min;
        }

        // max > a_max
        if (myValues.maxHeatSetpointLimit > myDefaultAbsoluteLimits.heat.max) {
          myValues.maxHeatSetpointLimit = myDefaultAbsoluteLimits.heat.max;
        }

        // min > a_max
        if (myValues.minHeatSetpointLimit > myDefaultAbsoluteLimits.heat.max) {
          myValues.minHeatSetpointLimit = myDefaultAbsoluteLimits.heat.min;
        }

        // max < a_min
        if (myValues.maxHeatSetpointLimit < myDefaultAbsoluteLimits.heat.min) {
          myValues.maxHeatSetpointLimit = myDefaultAbsoluteLimits.heat.max;
        }

        // max < min
        if (myValues.maxHeatSetpointLimit < myValues.minHeatSetpointLimit) {
          myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
        }

        // Check absolute and internal relational values for cool limits.
        // min < a_min
        if (myValues.minCoolSetpointLimit < myDefaultAbsoluteLimits.cool.min) {
          myValues.minCoolSetpointLimit = myDefaultAbsoluteLimits.cool.min;
        }

        // max > a_max
        if (myValues.maxCoolSetpointLimit > myDefaultAbsoluteLimits.cool.max) {
          myValues.maxCoolSetpointLimit = myDefaultAbsoluteLimits.cool.max;
        }

        // min > a_max
        if (myValues.minCoolSetpointLimit > myDefaultAbsoluteLimits.cool.max) {
          myValues.minCoolSetpointLimit = myDefaultAbsoluteLimits.cool.min;
        }

        // max < a_min
        if (myValues.maxCoolSetpointLimit < myDefaultAbsoluteLimits.cool.min) {
          myValues.maxCoolSetpointLimit = myDefaultAbsoluteLimits.cool.max;
        }

        // max < min
        if (myValues.minCoolSetpointLimit > myValues.maxCoolSetpointLimit) {
          myValues.minCoolSetpointLimit = myValues.maxCoolSetpointLimit;
        }

        // we have to adjust the whole range as there is not enough room for the deadband.
        if (
          myValues.minHeatSetpointLimit >
          myValues.maxCoolSetpointLimit - deadband
        ) {
          // dominant movement: to the right
          if (myDirection > 0) {
            // not enough room for deadband to shove everything to the right.
            if (
              myValues.minHeatSetpointLimit + deadband >
              myDefaultAbsoluteLimits.cool.max
            ) {
              // move min heat to left of abs max cool by deadband.
              myValues.minHeatSetpointLimit =
                myDefaultAbsoluteLimits.cool.max - deadband;
              myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
              myValues.maxCoolSetpointLimit = myDefaultAbsoluteLimits.cool.max;
              myValues.minCoolSetpointLimit = myDefaultAbsoluteLimits.cool.max;
            } else {
              // enough room to shove to the right.
              myValues.maxCoolSetpointLimit =
                myValues.minHeatSetpointLimit + deadband;
            }
          } else {
            // not enough room for deadband to shove everything to the left.
            if (
              myValues.maxCoolSetpointLimit - deadband <
              myDefaultAbsoluteLimits.heat.min
            ) {
              // move max cool to right of abs min heat by deadband.
              myValues.maxCoolSetpointLimit =
                myDefaultAbsoluteLimits.heat.min + deadband;
              myValues.minCoolSetpointLimit = myValues.maxCoolSetpointLimit;
              myValues.minHeatSetpointLimit = myDefaultAbsoluteLimits.heat.min;
              myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
            } else {
              // enough room to shove to the left.
              myValues.minHeatSetpointLimit =
                myValues.maxCoolSetpointLimit - deadband;
            }
          }
        }

        // min heat and min cool not honoring deadband
        if (
          myValues.minHeatSetpointLimit >
          myValues.minCoolSetpointLimit - deadband
        ) {
          if (myDirection > 0) {
            // need to shove min cool to right
            if (myMode === 'heat') {
              // if min cool + deadband > abs max cool (no room to shove it to the right)
              if (
                myValues.minCoolSetpointLimit + deadband >
                myDefaultAbsoluteLimits.cool.max
              ) {
                myValues.minCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max;
                myValues.maxCoolSetpointLimit = myValues.minCoolSetpointLimit;
                myValues.minHeatSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max - deadband;
                myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
              } else {
                myValues.minCoolSetpointLimit =
                  myValues.minHeatSetpointLimit + deadband;
              }
            } else {
              // no room to shove min heat to left
              console.log(
                '[Thermostat Setpoint AutoAdjust][Min heat at abs min] >>>>>>>>>>>>>> should not happen',
              );
              if (
                myValues.minHeatSetpointLimit - deadband <
                myDefaultAbsoluteLimits.heat.min
              ) {
                myValues.minHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min;
                myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
                myValues.minCoolSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min + deadband;
                myValues.maxCoolSetpointLimit = myValues.minCoolSetpointLimit;
              } else {
                myValues.minHeatSetpointLimit =
                  myValues.minCoolSetpointLimit - deadband;
              }
            }
          } else {
            // need to shove min cool to right
            if (myMode === 'heat') {
              // if min cool + deadband > abs max cool (no room to shove it to the right)
              console.log(
                '[Thermostat Setpoint AutoAdjust][Min cool at abs max] >>>>>>>>>>>>>> should not happen',
              );
              if (
                myValues.minCoolSetpointLimit + deadband >
                myDefaultAbsoluteLimits.cool.max
              ) {
                myValues.minCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max;
                myValues.maxCoolSetpointLimit = myValues.minCoolSetpointLimit;
                myValues.minHeatSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max - deadband;
                myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
              } else {
                myValues.minCoolSetpointLimit =
                  myValues.minHeatSetpointLimit + deadband;
              }
            } else {
              // no room to shove min heat to left
              if (
                myValues.minHeatSetpointLimit - deadband <
                myDefaultAbsoluteLimits.heat.min
              ) {
                myValues.minHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min;
                myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
                myValues.minCoolSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min + deadband;
                myValues.maxCoolSetpointLimit = myValues.minCoolSetpointLimit;
              } else {
                myValues.minHeatSetpointLimit =
                  myValues.minCoolSetpointLimit - deadband;
              }
            }
          }
        }

        // max heat and max cool not honoring deadband
        if (
          myValues.maxHeatSetpointLimit >
          myValues.maxCoolSetpointLimit - deadband
        ) {
          if (myDirection > 0) {
            // need to shove max cool to right
            if (myMode === 'heat') {
              // if max heat + deadband > abs max cool (no room to shove it to the right)
              if (
                myValues.maxHeatSetpointLimit + deadband >
                myDefaultAbsoluteLimits.cool.max
              ) {
                myValues.maxCoolSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max;
                myValues.minCoolSetpointLimit = myValues.maxCoolSetpointLimit;
                myValues.maxHeatSetpointLimit =
                  myDefaultAbsoluteLimits.cool.max - deadband;
                myValues.minHeatSetpointLimit = myValues.maxHeatSetpointLimit;
              } else {
                myValues.maxCoolSetpointLimit =
                  myValues.maxHeatSetpointLimit + deadband;
              }
            } else {
              console.warn(
                '[Thermostat Setpoint AutoAdjust][Max heat at abs max] >>>>>>>>>>>>>> this should not happen',
              );
            }
          } else {
            // need to shove max heat to left
            if (myMode === 'heat') {
              console.warn(
                '[Thermostat Setpoint AutoAdjust][Max heat at abs max] this should not happen',
              );
            } else {
              // no room to shove max heat to left
              if (
                myValues.maxHeatSetpointLimit - deadband <
                myDefaultAbsoluteLimits.heat.min
              ) {
                myValues.maxHeatSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min;
                myValues.minHeatSetpointLimit = myValues.maxHeatSetpointLimit;
                myValues.maxCoolSetpointLimit =
                  myDefaultAbsoluteLimits.heat.min + deadband;
                myValues.minCoolSetpointLimit = myValues.maxCoolSetpointLimit;
              } else {
                myValues.maxHeatSetpointLimit =
                  myValues.maxCoolSetpointLimit - deadband;
              }
            }
          }
        }
        // at this point, the min / max values are properly aligned within the absolute limits
        // and respect the deadband. Now for the setpoints.

        if (myValues.heatSetpoint < myValues.minHeatSetpointLimit) {
          myValues.heatSetpoint = myValues.minHeatSetpointLimit;
        }
        if (myValues.heatSetpoint > myValues.maxHeatSetpointLimit) {
          myValues.heatSetpoint = myValues.maxHeatSetpointLimit;
        }

        if (myValues.coolSetpoint < myValues.minCoolSetpointLimit) {
          myValues.coolSetpoint = myValues.minCoolSetpointLimit;
        }
        if (myValues.coolSetpoint > myValues.maxCoolSetpointLimit) {
          myValues.coolSetpoint = myValues.maxCoolSetpointLimit;
        }

        // setpoints are within respective scale limits. Now for the deadband ...

        if (myValues.heatSetpoint > myValues.coolSetpoint - deadband) {
          // can I move the cool setpoint without hitting the max cool limit?
          if (
            myValues.heatSetpoint + deadband >
            myValues.maxCoolSetpointLimit
          ) {
            // nope ... so, set cool to max cool limit and adjust heat setpoint
            if (
              myValues.maxCoolSetpointLimit - deadband <
              myValues.minHeatSetpointLimit
            ) {
              // this is an error condition. Should not happen. But ... so just set heat
              // to min heat setpoint limit, disregarding the deadband ... but log the issue.
              Notifier.warn(
                'Unsolvable setpoint configuration. Violating deadband to set config.',
              );
              myValues.coolSetpoint = myValues.maxCoolSetpointLimit;
              myValues.heatSetpoint = myValues.minHeatSetpointLimit;
            } else {
              // we have room to move the heat setpoint once we move the cool setpoint so do it.
              myValues.coolSetpoint = myValues.maxCoolSetpointLimit;
              myValues.heatSetpoint = myValues.maxCoolSetpointLimit - deadband;
            }
          } else {
            // yes... so, set cool setpoint to heat setpoint + deadband.
            myValues.coolSetpoint = myValues.heatSetpoint + deadband;
          }
        }
      } else if (myValues.operatingMode === 'Heat') {
        // Check absolute and internal relational values for heat limits.
        // min < a_min
        if (myValues.minHeatSetpointLimit < myDefaultAbsoluteLimits.heat.min) {
          myValues.minHeatSetpointLimit = myDefaultAbsoluteLimits.heat.min;
        }

        // max > a_max
        if (myValues.maxHeatSetpointLimit > myDefaultAbsoluteLimits.heat.max) {
          myValues.maxHeatSetpointLimit = myDefaultAbsoluteLimits.heat.max;
        }

        // min > a_max
        if (myValues.minHeatSetpointLimit > myDefaultAbsoluteLimits.heat.max) {
          myValues.minHeatSetpointLimit = myDefaultAbsoluteLimits.heat.min;
        }

        // max < a_min
        if (myValues.maxHeatSetpointLimit < myDefaultAbsoluteLimits.heat.min) {
          myValues.maxHeatSetpointLimit = myDefaultAbsoluteLimits.heat.max;
        }

        // max < min
        if (myValues.maxHeatSetpointLimit < myValues.minHeatSetpointLimit) {
          myValues.maxHeatSetpointLimit = myValues.minHeatSetpointLimit;
        }

        // adjust heat setpoint to ensure it is within setpoint limits.
        if (myValues.heatSetpoint < myValues.minHeatSetpointLimit) {
          myValues.heatSetpoint = myValues.minHeatSetpointLimit;
        }
        if (myValues.heatSetpoint > myValues.maxHeatSetpointLimit) {
          myValues.heatSetpoint = myValues.maxHeatSetpointLimit;
        }
      } else if (myValues.operatingMode === 'Cool') {
        // Check absolute and internal relational values for cool limits.
        // min < a_min
        if (myValues.minCoolSetpointLimit < myDefaultAbsoluteLimits.cool.min) {
          myValues.minCoolSetpointLimit = myDefaultAbsoluteLimits.cool.min;
        }

        // max > a_max
        if (myValues.maxCoolSetpointLimit > myDefaultAbsoluteLimits.cool.max) {
          myValues.maxCoolSetpointLimit = myDefaultAbsoluteLimits.cool.max;
        }

        // min > a_max
        if (myValues.minCoolSetpointLimit > myDefaultAbsoluteLimits.cool.max) {
          myValues.minCoolSetpointLimit = myDefaultAbsoluteLimits.cool.min;
        }

        // max < a_min
        if (myValues.maxCoolSetpointLimit < myDefaultAbsoluteLimits.cool.min) {
          myValues.maxCoolSetpointLimit = myDefaultAbsoluteLimits.cool.max;
        }

        // max < min
        if (myValues.minCoolSetpointLimit > myValues.maxCoolSetpointLimit) {
          myValues.minCoolSetpointLimit = myValues.maxCoolSetpointLimit;
        }

        // adjust cool setpoint to ensure it is within setpoint limits.
        if (myValues.coolSetpoint < myValues.minCoolSetpointLimit) {
          myValues.coolSetpoint = myValues.minCoolSetpointLimit;
        }
        if (myValues.coolSetpoint > myValues.maxCoolSetpointLimit) {
          myValues.coolSetpoint = myValues.maxCoolSetpointLimit;
        }
      }

      const changes: Record<string, number | string | boolean> = {};
      [
        'maxHeatSetpointLimit',
        'minHeatSetpointLimit',
        'maxCoolSetpointLimit',
        'minCoolSetpointLimit',
        'heatSetpoint',
        'coolSetpoint',
      ].forEach((key) => {
        const myKey = key as keyof CurrentAttributes;
        if (baseValues[myKey] !== myValues[myKey]) {
          changes[myKey] = myValues[myKey] as number;
        }
      });

      return changes;
    },
    [newValues, confirmedDevice, preferredUnits],
  );

  const changeTarget = (
    targetMode: 'heat' | 'cool' | 'setpoint',
    delta: number,
  ) => {
    if (!can('update', 'Thermostat') || requiresEditReload) return;
    if (powerTargetBlockReason) return;
    if (
      confirmedDevice?.isMitsubishiThermostat === true &&
      confirmedDevice?.source?.type === 'Conductor' &&
      (canEditDeviceLimits(confirmedDevice) ||
        confirmedDevice.deviceMetrics?.some(
          (entry) =>
            ['heatSetpoint', 'coolSetpoint', 'setpoint'].includes(
              entry?.key ?? '',
            ) &&
            (entry?.value as { valueStep?: number })?.valueStep !== undefined,
        ))
    ) {
      const key =
        targetMode === 'heat'
          ? 'heatSetpoint'
          : targetMode === 'cool'
            ? 'coolSetpoint'
            : 'setpoint';
      if (!canWriteSetpoint(key) || !newValues) return;
      const next = nextNativeTarget(
        confirmedDevice,
        newValues,
        key,
        delta,
        preferredUnits,
      );
      if (next !== undefined) handleChange({ [key]: next });
      return;
    }
    if (targetMode === 'setpoint') {
      if (!canWriteSetpoint('setpoint')) return;
      if (
        (!separatePower && originalValues?.operatingMode !== 'Auto') ||
        !newValues?.supportsSingleSetpointAutoMode ||
        newValues.operatingMode !== 'Auto'
      )
        return;
      const core = usesCoreCentidegreeTargets(confirmedDevice);
      const next = nextAutoTarget(
        newValues.setpoint,
        delta,
        core
          ? Math.max(
              newValues.minSetpoint ?? -Infinity,
              newValues.minHeatSetpointLimit ?? -Infinity,
              newValues.minCoolSetpointLimit ?? -Infinity,
            )
          : newValues.minSetpoint,
        core
          ? Math.min(
              newValues.maxSetpoint ?? Infinity,
              newValues.maxHeatSetpointLimit ?? Infinity,
              newValues.maxCoolSetpointLimit ?? Infinity,
            )
          : newValues.maxSetpoint,
        preferredUnits,
      );
      if (next) handleChange({ setpoint: next.display });
      return;
    }
    if (confirmedDevice?.isMitsubishiThermostat) {
      const metric = targetMode === 'heat' ? 'heatSetpoint' : 'coolSetpoint';
      if (!canWriteSetpoint(metric) || !Number.isFinite(newValues?.[metric]))
        return;
      // Controller-native validation remains authoritative. Never constrain or
      // shift another target using hidden synthetic Super limit/deadband values.
      const min =
        targetMode === 'heat'
          ? newValues?.minHeatSetpointLimit
          : newValues?.minCoolSetpointLimit;
      const max =
        targetMode === 'heat'
          ? newValues?.maxHeatSetpointLimit
          : newValues?.maxCoolSetpointLimit;
      const next = Number(newValues?.[metric]) + delta;
      if (
        (min !== undefined && next < min) ||
        (max !== undefined && next > max)
      )
        return;
      handleChange({ [metric]: next });
      return;
    }
    const myValues = { ...newValues };
    const isAutoMode = myValues.operatingMode === 'Auto';

    if (
      !canWriteSetpoint(targetMode === 'heat' ? 'heatSetpoint' : 'coolSetpoint')
    )
      return;
    const mode = targetMode;
    const deadband = preferredUnits === Celsius ? 1 : 2;
    const usesCustomLimits = myValues.useCustomSetpointLimits ?? false;

    const limits: {
      heat: { min: number; max: number };
      cool: { min: number; max: number };
    } = {
      heat: {
        min:
          (usesCustomLimits
            ? myValues.minHeatSetpointLimit
            : myValues.baseMinHeatSetpointLimit) ??
          defaultAbsoluteLimits[preferredUnits].heat.min,
        max:
          (usesCustomLimits
            ? myValues.maxHeatSetpointLimit
            : myValues.baseMaxHeatSetpointLimit) ??
          defaultAbsoluteLimits[preferredUnits].heat.max,
      },
      cool: {
        min:
          (usesCustomLimits
            ? myValues.minCoolSetpointLimit
            : myValues.baseMinCoolSetpointLimit) ??
          defaultAbsoluteLimits[preferredUnits].cool.min,
        max:
          (usesCustomLimits
            ? myValues.maxCoolSetpointLimit
            : myValues.baseMaxCoolSetpointLimit) ??
          defaultAbsoluteLimits[preferredUnits].cool.max,
      },
    };

    const newSetpoint: { heat: number; cool: number } = {
      heat: myValues.heatSetpoint as number,
      cool: myValues.coolSetpoint as number,
    };
    if (
      newSetpoint[mode] === undefined ||
      limits[mode].min === undefined ||
      limits[mode].max === undefined
    )
      return;
    if (
      isAutoMode &&
      (newSetpoint.heat === undefined ||
        newSetpoint.cool === undefined ||
        limits.heat.min === undefined ||
        limits.cool.max === undefined)
    )
      return;
    newSetpoint[mode] = newSetpoint[mode]! + delta;

    // Compare new setpoint to the limits for the selected properties/units/tstats
    // Only allow the change if within limits
    if (
      newSetpoint[mode] > limits[mode]?.max ||
      newSetpoint[mode] < limits[mode]?.min
    ) {
      // New setpoint value exceeds the limits - reject the change
      if (newSetpoint[mode] > limits[mode]?.max) {
        newSetpoint[mode] = limits[mode]?.max;
      } else {
        newSetpoint[mode] = limits[mode]?.min;
      }
      let warning = `minimum: ${formatTemperatureString(
        limits[mode].min,
        preferredUnits,
      )}`;
      if (newSetpoint[mode] > limits[mode].max) {
        warning = `maximum: ${formatTemperatureString(
          limits[mode].max,
          preferredUnits,
        )}`;
      }
      Notifier.warn(`${warning}`, `${mode} setpoint limit exceeded`);
    }
    // Ok, the new setpoint value is within the limits

    // If operatingMode is Auto, ensure the deadband of cool - heat <= deadband
    if (isAutoMode) {
      if (newSetpoint.cool - newSetpoint.heat < deadband) {
        const oppositeMetric =
          mode === 'cool' ? 'heatSetpoint' : 'coolSetpoint';
        if (!canWriteSetpoint(oppositeMetric)) {
          // A one-sided approval cannot move the other target to preserve deadband.
          // Refuse this step and retain the last valid draft.
          return;
        } else if (mode === 'cool') {
          // push down heat setpoint
          if (newSetpoint.cool - deadband < limits.heat.min) {
            // Deadband shift of heat setpoint exceeds min heat limit - reject the change
            newSetpoint.heat = limits.heat.min;
            Notifier.warn(
              `Deadband Violation: Heat setpoint minimum: ${formatTemperatureString(
                limits.heat.min,
                preferredUnits,
              )}`,
            );
          } else {
            newSetpoint.heat = newSetpoint.cool - deadband;
          }
        } else {
          // push up cool setpoint
          if (newSetpoint.heat + deadband > limits.cool.max) {
            // Deadband shift of cool setpoint exceeds max cool limit - reject the change
            newSetpoint.cool = limits.cool.max;

            Notifier.warn(
              `Deadband Violation: Cool setpoint maximum: ${formatTemperatureString(
                limits.cool.max,
                preferredUnits,
              )}`,
            );
          } else {
            newSetpoint.cool = newSetpoint.heat + deadband;
          }
        }
      }
    }

    // New setpoint(s) preserve the deadband (if necessary) and are within the
    // limits - save and update
    const changes: Record<string, number | string> = {};
    if (myValues.coolSetpoint !== newSetpoint.cool) {
      changes.coolSetpoint = newSetpoint.cool;
    }
    if (myValues.heatSetpoint !== newSetpoint.heat) {
      changes.heatSetpoint = newSetpoint.heat;
    }

    // Check whether the hasChanges flag needs to be updated
    handleChange(changes);
  };

  /**
   * handleChange: make changes to setTemperature values
  /* - handle edits of setpoints, operating mode, fan mode 
   * - use a copy of current values to avoid being overwritten by subscription updates
   */
  const handleChange = (changes: Partial<CurrentAttributes>) => {
    if (requiresEditReload || !can('update', 'Thermostat')) return;
    if (
      powerTargetBlockReason &&
      Object.keys(changes).some((key) =>
        ['heatSetpoint', 'coolSetpoint', 'setpoint'].includes(key),
      )
    )
      return;
    const myChanges = { ...changes };
    const baseline = editBaseline.current ?? originalValues;
    if (separatePower && baseline && changes.operatingMode) {
      const defaults = getSharedTargetDefaults(baseline, changes.operatingMode);
      for (const key of ['heatSetpoint', 'coolSetpoint', 'setpoint'] as const) {
        if (!Number.isFinite(newValues?.[key]) && defaults[key] !== undefined)
          myChanges[key] = defaults[key];
      }
    }
    const myValues = { ...newValues, ...myChanges } as CurrentAttributes;

    setNewValues((current) => {
      return { ...current, ...myChanges } as CurrentAttributes;
    });

    let changesDetected = false;
    if (newValues && baseline) {
      const changedKeys = Object.keys(baseline).find((key) => {
        const myKey: keyof CurrentAttributes =
          key as unknown as keyof CurrentAttributes;
        return !_.isEqual(myValues[myKey], baseline[myKey]);
      });
      if (changedKeys) {
        changesDetected = true;
      }
    }

    if (changesDetected) {
      setViewMode(ViewMode.EDIT);
    } else {
      setViewMode(ViewMode.EDIT_UNCHANGED);
    }
  };

  const handleScheduleChange = (
    operation: 'edit_setpoints' | 'edit_time',
    dayOfWeek: DayOfWeek,
    timeOfDay: TimeOfDay,
    newTimeOfDay?: TimeOfDay,
    scheduleSetting?: MyScheduleSetting,
  ) => {
    // Create a copy of the current schedule object, or a new schedule object if none exists
    const _newSchedule = _.cloneDeep(newSchedule);
    if (newSchedule && newSchedule.value) {
      _newSchedule.value = _.cloneDeep(newSchedule.value);
    } else {
      _newSchedule.value = _.cloneDeep(EmptyWeeklySchedule);
    }

    // Perform the specified update, for the specified day of the week
    switch (operation) {
      case 'edit_setpoints':
        _newSchedule.value[dayOfWeek] = {
          ..._newSchedule.value[dayOfWeek],
          [timeOfDay]: scheduleSetting ?? {},
        };
        break;
      case 'edit_time':
        if (newTimeOfDay && scheduleSetting) {
          const timeKeys = Object.keys(_newSchedule.value[dayOfWeek])
            .map((timeString) => parseInt(timeString, 10))
            .sort((a, b) => a - b);
          const currentTimeIndex = timeKeys.indexOf(parseInt(timeOfDay, 10));
          const minTimeLimit =
            currentTimeIndex === 0 ? -1 : timeKeys[currentTimeIndex - 1];
          const maxTimeLimit =
            currentTimeIndex === timeKeys.length - 1
              ? 24 * 60 * 60 * 1000
              : timeKeys[currentTimeIndex + 1];

          const proposedNewTimeOfDay = parseInt(newTimeOfDay, 10);
          if (proposedNewTimeOfDay <= minTimeLimit) {
            // New timeOfDay value is less than the previous timeOfDay value - reject the change
            Notifier.warn(
              `You must choose a time value between the times set for the previous and next events in the schedule.`,
            );
            throw new Error('Out of sequence time value');
          } else if (proposedNewTimeOfDay >= maxTimeLimit) {
            // New timeOfDay value is greater than the next timeOfDay value - reject the change
            Notifier.warn(
              `You must choose a time value between the times set for the previous and next events in the schedule.`,
            );
            throw new Error('Out of sequence time value');
          } else {
            delete _newSchedule.value[dayOfWeek][timeOfDay];
            _newSchedule.value[dayOfWeek][newTimeOfDay] =
              _.cloneDeep(scheduleSetting);
          }
        }
        break;
    }

    // Ensure the update is applied to all appropriate days of the week, according to the schedule type
    const updatedDailySchedule = _newSchedule.value[dayOfWeek];
    if (scheduleType === EnumScheduleType.SameEveryDay) {
      // Apply update to Sunday's schedule to all other days of the week
      _newSchedule.value[DayOfWeek.MO] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.TU] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.WE] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.TH] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.FR] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.SA] = updatedDailySchedule;
    } else if (scheduleType === EnumScheduleType.WeekdayWeekend) {
      if (dayOfWeek === DayOfWeek.SU) {
        // Apply update to Sunday's schedule to the rest of the weekend
        _newSchedule.value[DayOfWeek.SA] = updatedDailySchedule;
      } else if (dayOfWeek === DayOfWeek.MO) {
        // Apply update to Monday's schedule to the rest of the weekdays
        _newSchedule.value[DayOfWeek.TU] = updatedDailySchedule;
        _newSchedule.value[DayOfWeek.WE] = updatedDailySchedule;
        _newSchedule.value[DayOfWeek.TH] = updatedDailySchedule;
        _newSchedule.value[DayOfWeek.FR] = updatedDailySchedule;
      }
    }

    const hasChanges = !areSchedulesEqual(
      schedule?.value ?? EmptyWeeklySchedule,
      _newSchedule.value,
    );

    setNewSchedule(_newSchedule as MyScheduleObject);
    setViewMode(hasChanges ? ViewMode.SCHEDULE : ViewMode.SCHEDULE_UNCHANGED);
  };

  const handleScheduleTypeChange = (scheduleType: EnumScheduleType) => {
    const _newScheduleClone = {
      ..._.cloneDeep(newSchedule),
      value: convertToPreferredWeeklyScheduleUnits(
        _.cloneDeep(newSchedule.value),
        TemperatureUnit.C,
        user?.preferredUnits ?? TemperatureUnit.C,
      ),
    };

    const _scheduleClone = {
      ..._.cloneDeep(schedule),
      value: convertToPreferredWeeklyScheduleUnits(
        _.cloneDeep(schedule.value),
        TemperatureUnit.C,
        user?.preferredUnits ?? TemperatureUnit.C,
      ),
    };

    const _EmptyWeeklyScheduleClone = _.cloneDeep(EmptyWeeklySchedule);

    const _dailyScheduleDefaultClone = _.cloneDeep(_dailyScheduleDefault);

    if (scheduleType === EnumScheduleType.SameEveryDay) {
      // Set all daily schedules to the same value
      let _dailySchedule: MyDailySchedule = _EmptyWeeklyScheduleClone;

      if (
        _.isEqual(_scheduleClone.value, EmptyWeeklySchedule) &&
        _.isEqual(_newScheduleClone.value, EmptyWeeklySchedule)
      ) {
        _dailySchedule = _dailyScheduleDefaultClone;
      } else if (
        !_.isEqual(_scheduleClone.value, EmptyWeeklySchedule) &&
        _.isEqual(_newScheduleClone.value, EmptyWeeklySchedule)
      ) {
        _dailySchedule = _scheduleClone.value[DayOfWeek.SU];
      } else {
        _dailySchedule = _newScheduleClone.value[DayOfWeek.SU];
      }

      const dailySchedule = _dailySchedule;

      _newScheduleClone.value[DayOfWeek.SU] = _.cloneDeep(dailySchedule);
      _newScheduleClone.value[DayOfWeek.MO] = _.cloneDeep(dailySchedule);
      _newScheduleClone.value[DayOfWeek.TU] = _.cloneDeep(dailySchedule);
      _newScheduleClone.value[DayOfWeek.WE] = _.cloneDeep(dailySchedule);
      _newScheduleClone.value[DayOfWeek.TH] = _.cloneDeep(dailySchedule);
      _newScheduleClone.value[DayOfWeek.FR] = _.cloneDeep(dailySchedule);
      _newScheduleClone.value[DayOfWeek.SA] = _.cloneDeep(dailySchedule);
    } else if (scheduleType === EnumScheduleType.WeekdayWeekend) {
      let _weekendSchedule: MyDailySchedule = _.cloneDeep(EmptyWeeklySchedule);
      let _weekdaySchedule: MyDailySchedule = _.cloneDeep(EmptyWeeklySchedule);

      // Set all weekday and weekend schedules to the same values
      if (
        _.isEqual(_scheduleClone.value, EmptyWeeklySchedule) &&
        _.isEqual(_newScheduleClone.value, EmptyWeeklySchedule)
      ) {
        _weekendSchedule = _dailyScheduleDefaultClone;
        _weekdaySchedule = _dailyScheduleDefaultClone;
      } else if (
        !_.isEqual(_scheduleClone.value, EmptyWeeklySchedule) &&
        _.isEqual(_newScheduleClone.value, EmptyWeeklySchedule)
      ) {
        _weekendSchedule = _scheduleClone.value[DayOfWeek.SU];
        _weekdaySchedule = _scheduleClone.value[DayOfWeek.MO];
      } else {
        _weekendSchedule = _newScheduleClone.value[DayOfWeek.SU];
        _weekdaySchedule = _newScheduleClone.value[DayOfWeek.MO];
      }

      _newScheduleClone.value[DayOfWeek.MO] = _.cloneDeep(_weekdaySchedule);
      _newScheduleClone.value[DayOfWeek.TU] = _.cloneDeep(_weekdaySchedule);
      _newScheduleClone.value[DayOfWeek.WE] = _.cloneDeep(_weekdaySchedule);
      _newScheduleClone.value[DayOfWeek.TH] = _.cloneDeep(_weekdaySchedule);
      _newScheduleClone.value[DayOfWeek.FR] = _.cloneDeep(_weekdaySchedule);
      _newScheduleClone.value[DayOfWeek.SA] = _.cloneDeep(_weekendSchedule);
      _newScheduleClone.value[DayOfWeek.SU] = _.cloneDeep(_weekendSchedule);
    } else if (scheduleType === EnumScheduleType.Custom) {
      const defaultWeeklySchedule: MyWeeklySchedule = {
        [DayOfWeek.SU]: _.cloneDeep(_dailyScheduleDefault),
        [DayOfWeek.MO]: _.cloneDeep(_dailyScheduleDefault),
        [DayOfWeek.TU]: _.cloneDeep(_dailyScheduleDefault),
        [DayOfWeek.WE]: _.cloneDeep(_dailyScheduleDefault),
        [DayOfWeek.TH]: _.cloneDeep(_dailyScheduleDefault),
        [DayOfWeek.FR]: _.cloneDeep(_dailyScheduleDefault),
        [DayOfWeek.SA]: _.cloneDeep(_dailyScheduleDefault),
      };

      let _weeklySchedule: MyWeeklySchedule = _.cloneDeep(EmptyWeeklySchedule);

      if (
        _.isEqual(_scheduleClone.value, EmptyWeeklySchedule) &&
        _.isEqual(_newScheduleClone, EmptyWeeklySchedule)
      ) {
        _weeklySchedule = defaultWeeklySchedule;
      } else if (
        !_.isEqual(_scheduleClone.value, EmptyWeeklySchedule) &&
        _.isEqual(_newScheduleClone.value, EmptyWeeklySchedule)
      ) {
        _weeklySchedule = _scheduleClone.value;
      } else {
        _weeklySchedule = _newScheduleClone.value;
      }

      _newScheduleClone.value = _weeklySchedule;
    } else if (scheduleType === EnumScheduleType.None) {
      _newScheduleClone.value = _.cloneDeep(EmptyWeeklySchedule);
    }

    const hasRecentChanges = !areSchedulesEqual(
      _scheduleClone.value,
      _newScheduleClone.value,
    );

    if (device) {
      const limits = {
        cool: {
          min: device.minCoolSetpointLimit?.value as number | undefined, // Types must be a number or undefined
          max: device.maxCoolSetpointLimit?.value as number | undefined,
        },
        heat: {
          min: device.minHeatSetpointLimit?.value as number | undefined,
          max: device.maxHeatSetpointLimit?.value as number | undefined,
        },
      };

      const adjustedSchedule = _.cloneDeep(_newScheduleClone);

      adjustedSchedule.value = applySetpointLimitToWeeklyScheduleValues(
        _newScheduleClone.value as MyWeeklySchedule | undefined,
        limits,
      ) as MyWeeklySchedule;

      if (user) {
        if (adjustedSchedule.value !== undefined) {
          adjustedSchedule.value = convertToPreferredWeeklyScheduleUnits(
            adjustedSchedule.value,
            user.preferredUnits,
            TemperatureUnit.C,
          );
        }
      }

      setNewSchedule(adjustedSchedule);
    } else {
      setNewSchedule(_newScheduleClone);
    }

    setViewMode(
      hasRecentChanges ? ViewMode.SCHEDULE : ViewMode.SCHEDULE_UNCHANGED,
    );

    setScheduleType(scheduleType);
  };

  /**
   * handleMenuClick: set the ViewMode based on a given MenuAction
   */
  const handleMenuClick = (action: MenuAction) => {
    switch (action) {
      case MenuAction.CANCEL:
        pendingValueSave.current = undefined;
        editContext.current = undefined;
        editBaseline.current = undefined;
        setEditInvalidated(false);
        setLimitDraftInvalid(false);
        setViewMode(ViewMode.NORMAL);
        setNewValues(undefined);
        break;
      case MenuAction.EDIT_SCHEDULE:
        if (
          !(device as MyThermostat & { supportsScheduling?: boolean })
            ?.supportsScheduling
        ) {
          Notifier.warn('Scheduling is not supported for this thermostat.');
          break;
        }
        updateAttempt.current += 1;
        setViewMode(ViewMode.SCHEDULE_UNCHANGED);
        const scheduleClone = _.cloneDeep(schedule);
        const newScheduleClone = _.cloneDeep(newSchedule);
        if (device?.useCustomSchedule) {
          if (!device?.thermostatScheduleTemplateId) {
            if (newSchedule && newSchedule.value) {
              newScheduleClone.value = (scheduleClone && {
                ..._.cloneDeep(scheduleClone.value),
              }) || {
                ..._.cloneDeep(EmptyWeeklySchedule),
              };
              setNewSchedule(newScheduleClone);
              if (_.isEqual(newScheduleClone.value, EmptyWeeklySchedule)) {
                setScheduleType(EnumScheduleType.None);
              } else if (device.scheduleType) {
                setScheduleType(device.scheduleType);
              } else {
                setScheduleType(EnumScheduleType.None);
              }
            } else {
              setNewSchedule({ ...scheduleClone } as MyScheduleObject);
            }
          }
        }
        break;
      case MenuAction.EDIT_VALUES:
        updateAttempt.current += 1;
        pendingValueSave.current = undefined;
        if (!can('update', 'Thermostat')) return;
        editContext.current = confirmedContext;
        editBaseline.current = originalValues
          ? { ...originalValues }
          : undefined;
        setEditInvalidated(false);
        setLimitDraftInvalid(false);
        const changes =
          confirmedDevice?.isMitsubishiThermostat ||
          (originalValues?.supportsSingleSetpointAutoMode &&
            originalValues.operatingMode === 'Auto')
            ? {}
            : conformToSetpointLimits(
                originalValues,
                undefined,
                undefined,
                undefined,
                true,
              );
        if (Object.keys(changes ?? {}).length > 0) {
          Notifier.warn(
            'Current values have been adjusted in the editor to comply with pre-set limits and rules.',
          );
        }
        setNewValues({ ...originalValues, ...changes } as CurrentAttributes);
        setViewMode(ViewMode.EDIT_UNCHANGED);
        break;
      case MenuAction.REVERT_CHANGES:
        pendingValueSave.current = undefined;
        editContext.current = undefined;
        editBaseline.current = undefined;
        setEditInvalidated(false);
        setLimitDraftInvalid(false);
        setViewMode(ViewMode.NORMAL);
        setNewValues(undefined);
        setNewSchedule(DefaultSchedule);
        setScheduleType(data?.thermostatById?.scheduleType ?? undefined);
        break;
      case MenuAction.SAVE_CHANGES:
        if (
          !can('update', 'Thermostat') ||
          requiresEditReload ||
          limitDraftInvalid
        )
          return;
        if (powerPending) return;
        if (device && originalValues && newValues) {
          const contextAtSave = confirmedContext;
          const savingValues = isEditingValues;
          const baselineValues = savingValues
            ? (editBaseline.current ?? originalValues)
            : originalValues;
          pendingValueSave.current = savingValues
            ? { context: contextAtSave, invalidated: false }
            : undefined;
          recoveringFromFailedUpdate.current = false;
          setViewMode(ViewMode.UPDATING);
          setTimeout(() => {
            if (
              savingValues &&
              (pendingValueSave.current?.invalidated ||
                contextAtSave !== latestConfirmedContext.current)
            ) {
              setEditInvalidated(true);
              setViewMode(ViewMode.EDIT);
              return;
            }
            // Revert to normal view mode if update doesn't complete after 30s.
            // While most thermostat updates should complete in a matter of seconds,
            // occasionally the zigbee network is slow and the update takes longer.
            // If we revert too soon while in AWAITING view mode, this might trigger
            // the "Thermostat being updated on another device" warning, because the
            // `hasPendingUpdates` flag hasn't been cleared on the server yet.

            let command;
            try {
              command = buildThermostatControlEdit(
                baselineValues,
                {
                  ...newValues,
                  fanMode: device.supportsFanOperations
                    ? newValues.fanMode
                    : baselineValues.fanMode,
                },
                powerSnapshot ?? undefined,
                preferredUnits,
                confirmedDevice,
              );
            } catch (error) {
              pendingValueSave.current = undefined;
              setViewMode(ViewMode.EDIT);
              Notifier.error(
                error instanceof Error
                  ? error.message
                  : 'Unable to submit thermostat update.',
              );
              return;
            }
            const updates: NonNullable<
              UpdateThermostatMutationVariables['updates']
            > = command.updates;

            if (
              newValues.keypadLockoutLevel !== baselineValues.keypadLockoutLevel
            ) {
              updates.keypadLockoutLevel = parseInt(
                newValues.keypadLockoutLevel ?? '0',
              ) as InputMaybe<number>;
            }

            if (
              newValues.minHeatSetpointLimit !==
              baselineValues.minHeatSetpointLimit
            ) {
              updates.minHeatSetpointLimit = convertLimitTemperature(
                newValues.minHeatSetpointLimit,
                Celsius,
                preferredUnits,
              ) as InputMaybe<number>;
            }

            if (
              newValues.maxHeatSetpointLimit !==
              baselineValues.maxHeatSetpointLimit
            ) {
              updates.maxHeatSetpointLimit = convertLimitTemperature(
                newValues.maxHeatSetpointLimit,
                Celsius,
                preferredUnits,
              ) as InputMaybe<number>;
            }

            if (
              newValues.minCoolSetpointLimit !==
              baselineValues.minCoolSetpointLimit
            ) {
              updates.minCoolSetpointLimit = convertLimitTemperature(
                newValues.minCoolSetpointLimit,
                Celsius,
                preferredUnits,
              ) as InputMaybe<number>;
            }

            if (
              newValues.maxCoolSetpointLimit !==
              baselineValues.maxCoolSetpointLimit
            ) {
              updates.maxCoolSetpointLimit = convertLimitTemperature(
                newValues.maxCoolSetpointLimit,
                Celsius,
                preferredUnits,
              ) as InputMaybe<number>;
            }

            if (newValues.progMode !== baselineValues.progMode) {
              updates.progMode = newValues.progMode as InputMaybe<string>;
            }

            if (newValues.holdState !== baselineValues.holdState) {
              updates.holdState = newValues.holdState as InputMaybe<string>;
            }

            if (
              device.supportsShowFanButton &&
              newValues.showFanButton !== baselineValues.showFanButton
            ) {
              updates.showFanButton =
                newValues?.showFanButton as InputMaybe<boolean>;
            }

            if (
              device.supportsSystemType &&
              newValues.systemType !== baselineValues.systemType
            ) {
              updates.systemType = newValues?.systemType as InputMaybe<string>;
            }

            if (newSchedule && schedule) {
              const hasChange = !areSchedulesEqual(
                schedule.value || EmptyWeeklySchedule,
                newSchedule.value || EmptyWeeklySchedule,
              );
              if (hasChange) {
                // Ensure all temperature values are converted to Celsius before saving
                const scheduleUpdate: MyWeeklySchedule =
                  _.cloneDeep(EmptyWeeklySchedule);
                for (const day in newSchedule.value) {
                  const dailySchedule = newSchedule.value[day as DayOfWeek];
                  for (const transition in dailySchedule) {
                    const scheduleSetting = dailySchedule[transition];
                    scheduleUpdate[day as DayOfWeek][transition] = {};
                    if (scheduleSetting.maximum !== undefined) {
                      scheduleUpdate[day as DayOfWeek][transition].maximum =
                        convertLimitTemperature(
                          scheduleSetting.maximum,
                          Celsius,
                          preferredUnits,
                        );
                    }

                    if (scheduleSetting.minimum !== undefined) {
                      scheduleUpdate[day as DayOfWeek][transition].minimum =
                        convertLimitTemperature(
                          scheduleSetting.minimum,
                          Celsius,
                          preferredUnits,
                        );
                    }
                  }
                }
                updates.schedule = scheduleUpdate;
              }
            }

            if (
              savingValues &&
              (
                [
                  'heatSetpoint',
                  'coolSetpoint',
                  'setpoint',
                  'operatingMode',
                  'fanMode',
                  'fanSpeed',
                  'airDirection',
                ] as const
              ).some(
                (metric) =>
                  Object.prototype.hasOwnProperty.call(updates, metric) &&
                  !canWriteSetpoint(metric),
              )
            ) {
              setEditInvalidated(true);
              setViewMode(ViewMode.EDIT);
              return;
            }
            // Fields that can be modified in the DB, but are not attributes
            // that can be configured on the physical thermostat
            const fields: ThermostatUpdateFields = {};
            if (
              !_.isNil(newValues.useCustomSetpointLimits) &&
              newValues.useCustomSetpointLimits !==
                baselineValues.useCustomSetpointLimits
            ) {
              fields.useCustomSetpointLimits =
                newValues.useCustomSetpointLimits;
            }

            // Check if schedule object contains custom values
            // If so then use custom schedule is applied
            if (_.isEqual(newSchedule?.value, EmptyWeeklySchedule)) {
              fields.useCustomSchedule = false;
            } else {
              fields.useCustomSchedule = true;
            }
            if (device?.deviceId) {
              // Hidden Mitsubishi settings must never be manufactured by the
              // legacy full-form editor before separate power is commissioned.
              const policyEditable = canEditDeviceLimits(device);
              if (
                LIMIT_FIELDS.some((field) => field in updates) ||
                fields.useCustomSetpointLimits !== undefined
              ) {
                const issues = validateDisplayLimits(
                  newValues,
                  device,
                  preferredUnits,
                );
                if (issues.length) {
                  pendingValueSave.current = undefined;
                  setViewMode(ViewMode.EDIT);
                  Notifier.error(
                    issues.map((issue) => issue.message).join(' '),
                  );
                  return;
                }
              }
              if (device.isMitsubishiThermostat) {
                for (const key of Object.keys(updates))
                  if (
                    ![
                      'heatSetpoint',
                      'coolSetpoint',
                      'setpoint',
                      'operatingMode',
                      'fanMode',
                      'fanSpeed',
                      ...(separatePower ? ['powerMode', 'airDirection'] : []),
                      ...(policyEditable ? LIMIT_FIELDS : []),
                    ].includes(key)
                  )
                    delete (updates as Record<string, unknown>)[key];
                for (const key of Object.keys(fields))
                  if (!(policyEditable && key === 'useCustomSetpointLimits'))
                    delete (fields as Record<string, unknown>)[key];
              }
              if (!Object.keys(updates).length && !Object.keys(fields).length) {
                pendingValueSave.current = undefined;
                setNewValues(undefined);
                setViewMode(ViewMode.NORMAL);
                return;
              }
              if (
                device.supportsScheduling !== false &&
                Object.keys(updates).some((a) =>
                  [
                    'maxCoolSetpointLimit',
                    'minCoolSetpointLimit',
                    'maxHeatSetpointLimit',
                    'minHeatSetpointLimit',
                  ].includes(a),
                )
              ) {
                if (device?.schedule) {
                  if (!_.isEqual(device.schedule.value, EmptyWeeklySchedule)) {
                    updates.schedule = device.schedule.value;
                    fields.useCustomSchedule = true;
                    fields.thermostatScheduleTemplateId = '';

                    setUpdateThermostatVariables({
                      id: device.deviceId,
                      updates,
                      fields,
                      options: command.options,
                    });
                    setOpenThermostatScheduleUpdateDialog(true);
                  } else {
                    handleUpdateThermostat(
                      {
                        id: device.deviceId,
                        updates,
                        fields,
                        options: command.options,
                      },
                      undefined,
                    );
                  }
                } else {
                  handleUpdateThermostat(
                    {
                      id: device.deviceId,
                      updates,
                      fields,
                      options: command.options,
                    },
                    undefined,
                  );
                }
              } else {
                handleUpdateThermostat(
                  {
                    id: device.deviceId,
                    updates,
                    fields,
                    options: command.options,
                  },
                  undefined,
                );
              }
            }
          }, 500);
        }
        break;
    }
  };

  //TODO: Calvin, refactor this method to include adjustScheduleToConformToSetpointLimits in variables state
  const handleUpdateThermostat = (
    variables: UpdateThermostatMutationVariables | undefined,
    adjustScheduleToConformToSetpointLimits: boolean | undefined,
  ) => {
    if (
      pendingValueSave.current &&
      (pendingValueSave.current.invalidated ||
        pendingValueSave.current.context !== latestConfirmedContext.current)
    ) {
      setEditInvalidated(true);
      setViewMode(ViewMode.EDIT);
      return;
    }
    if (variables && confirmedDevice?.supportsScheduling === false) {
      variables = {
        ...variables,
        updates: _.omit(variables.updates, 'schedule'),
        fields: _.omit(variables.fields, [
          'useCustomSchedule',
          'thermostatScheduleTemplateId',
          'adjustScheduleToConformToSetpointLimits',
        ]),
      };
      adjustScheduleToConformToSetpointLimits = undefined;
    }
    if (
      variables?.updates?.schedule &&
      adjustScheduleToConformToSetpointLimits !== false
    ) {
      const defaults = deviceDefaultLimits(confirmedDevice);
      const limits = {
        heat: {
          min:
            variables.updates.minHeatSetpointLimit ??
            device?.minHeatSetpointLimit?.value ??
            defaults.minHeatSetpointLimit,
          max:
            variables.updates.maxHeatSetpointLimit ??
            device?.maxHeatSetpointLimit?.value ??
            defaults.maxHeatSetpointLimit,
        },
        cool: {
          min:
            variables.updates.minCoolSetpointLimit ??
            device?.minCoolSetpointLimit?.value ??
            defaults.minCoolSetpointLimit,
          max:
            variables.updates.maxCoolSetpointLimit ??
            device?.maxCoolSetpointLimit?.value ??
            defaults.maxCoolSetpointLimit,
        },
      };
      const proposed = adjustScheduleToConformToSetpointLimits
        ? applySetpointLimitToWeeklyScheduleValues(
            variables.updates.schedule,
            limits,
          )
        : variables.updates.schedule;
      if (validateScheduleLimitValues(proposed, limits).length) {
        Notifier.error(
          'The complete schedule is outside the available equipment policy.',
        );
        setViewMode(ViewMode.EDIT);
        return;
      }
    }
    pendingValueSave.current = undefined;
    const attempt = ++updateAttempt.current;
    const updateTimer = powerSnapshot
      ? undefined
      : setTimeout(() => {
          if (attempt === updateAttempt.current) setViewMode(ViewMode.NORMAL);
        }, 30000);
    if (variables !== undefined) {
      updateThermostat({
        variables: {
          id: variables.id,
          updates: variables.updates,
          fields:
            powerSnapshot &&
            variables.fields?.useCustomSetpointLimits === undefined
              ? undefined
              : {
                  ...variables.fields,
                  adjustScheduleToConformToSetpointLimits,
                },
          options: variables.options ?? {},
        },
      })
        .then((result) => {
          if (attempt !== updateAttempt.current) {
            clearTimeout(updateTimer);
            return;
          }
          if (result.errors) {
            // An error occurred, throw it and handle it below
            throw new Error(result.errors[0].message);
          } else if (result.data?.updateThermostat.hasPendingUpdates) {
            // Thermostat has pending updates, clear the timer and wait for
            // those updates to complete
            clearTimeout(updateTimer);
          } else {
            // Thermostat has no pending updates. This is rare, but can happen
            // if the server deems that all given attributes are already in the
            // correct state, in which case it will return without error, but
            // skip sending any request to configure the thermostat. In this case,
            // we can consider the update to have completed successfully.
            Notifier.info('Thermostat update completed');
            clearTimeout(updateTimer);
            setNewValues(undefined);
            // setNewSchedule(DefaultSchedule);
            setViewMode(ViewMode.NORMAL);
          }
        })
        .catch((error) => {
          if (attempt !== updateAttempt.current) {
            clearTimeout(updateTimer);
            return;
          }
          console.log(
            '[Update Thermostat Values] Got error during update:',
            error,
          );
          Notifier.error(
            'Received Error while updating Thermostat. Please try again or contact support if the problem persists.',
          );
          clearTimeout(updateTimer);
          recoveringFromFailedUpdate.current = true;
          failedUpdateData.current = latestQueryData.current;
          setNewValues(undefined);
          // setSchedule(undefined);
          // setNewSchedule(DefaultSchedule);
          setViewMode(ViewMode.NORMAL);
        });
    }
  };

  const handleClearSchedule = () => {
    if (device?.deviceId) {
      const updateTimer = setTimeout(() => setViewMode(ViewMode.NORMAL), 30000);
      recoveringFromFailedUpdate.current = false;
      setViewMode(ViewMode.UPDATING);
      updateThermostat({
        variables: {
          id: device.deviceId,
          updates: {
            schedule: EmptyWeeklySchedule, // Pass in an empty schedule to clear the current one
          },
          fields: {
            useCustomSchedule: false,
            thermostatScheduleTemplateId: '',
          },
          options: {},
        },
      })
        .then((result) => {
          if (result.errors) {
            // An error occurred, throw it and handle it below
            throw new Error(result.errors[0].message);
          } else if (result.data?.updateThermostat.hasPendingUpdates) {
            // Thermostat has pending updates, clear the timer and wait for
            // those updates to complete
            clearTimeout(updateTimer);
            setNewValues(undefined);
            setNewSchedule(DefaultSchedule);
            setScheduleType(EnumScheduleType.None);
          } else {
            // Thermostat has no pending updates. This is rare, but can happen
            // if the server deems that all green attributes are already in the
            // correct state, in which case it will return without error, but
            // skip sending any request to configure the thermostat. In this case,
            // we can consider the update to have completed successfully.
            Notifier.info('Thermostat update completed');
            clearTimeout(updateTimer);
            setNewValues(undefined);
            setNewSchedule(DefaultSchedule);
            setScheduleType(EnumScheduleType.None);
            setViewMode(ViewMode.NORMAL);
          }
        })
        .catch((error) => {
          console.log(
            '[Update Thermostat Values] Got error during update:',
            error,
          );
          Notifier.error(
            'Received Error while updating Thermostat. Please try again or contact support if the problem persists.',
          );
          clearTimeout(updateTimer);
          recoveringFromFailedUpdate.current = true;
          failedUpdateData.current = latestQueryData.current;
          setNewValues(undefined);
          // setSchedule(undefined);
          setNewSchedule(DefaultSchedule);
          setViewMode(ViewMode.NORMAL);
        });
    }
  };

  /**
   * display the status message when hovering over the status icon
   */
  const showStatusMessage = (msg: string) => {
    setStatusMessage(msg);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // PLM abort handlers
  const handlePlmAbortCurrent = async () => {
    try {
      if (!canAccessPlm) {
        Notifier.error('You do not have permission to cancel PLM cycles');
        return;
      }

      if (!device?.deviceId) {
        Notifier.error('Device ID not found');
        return;
      }

      const result = await abortPlmCurtailmentTask({
        variables: {
          deviceId: device.deviceId,
          cancelAll: false,
        },
      });

      if (result.data?.abortPlmCurtailmentTask) {
        Notifier.success('Current PLM cycle cancelled successfully');
        setPlmAbortDialogOpen(false);
      } else {
        Notifier.error('Failed to cancel current PLM cycle');
      }
    } catch (error) {
      console.error('Error aborting current PLM cycle:', error);
      Notifier.error('Failed to cancel current PLM cycle');
    }
  };

  const handlePlmAbortAll = async () => {
    try {
      if (!canAccessPlm) {
        Notifier.error('You do not have permission to cancel PLM cycles');
        return;
      }

      if (!device?.deviceId) {
        Notifier.error('Device ID not found');
        return;
      }

      const result = await abortPlmCurtailmentTask({
        variables: {
          deviceId: device.deviceId,
          cancelAll: true,
        },
      });

      if (result.data?.abortPlmCurtailmentTask) {
        Notifier.success('All remaining PLM cycles cancelled successfully');
        setPlmAbortDialogOpen(false);
      } else {
        Notifier.error('Failed to cancel remaining PLM cycles');
      }
    } catch (error) {
      console.error('Error aborting all PLM cycles:', error);
      Notifier.error('Failed to cancel remaining PLM cycles');
    }
  };

  // const hasAlerts =
  //   (device?.deviceAlerts?.length && device?.deviceAlerts?.length > 0) ||
  //   (device?.sourceAlerts?.length && device?.sourceAlerts?.length > 0);

  return originalValues && device ? (
    loading ? (
      <Card className="thermostat-loading-container">
        <CardContent className="thermostat-loading-2">
          <CircularProgress />
        </CardContent>
      </Card>
    ) : (
      <Card
        data-testid={`thermostat-widget-${device.deviceId}`}
        sx={{
          border: hasAlerts
            ? '1px solid lightgray'
            : isPlmActive
              ? '1px solid darkgreen'
              : '1px solid lightgray',
          boxShadow: 2,
          p: 0,
          width: '100%',
        }}
      >
        <CardHeader
          style={{
            backgroundColor: isPlmActive
              ? 'rgba(128, 128, 128, 0.68)'
              : '#5eb85ead',
            padding: '4px',
          }}
          avatar={
            <Avatar
              sx={{
                backgroundColor: 'transparent',
                padding: '0px',
                width: '25px',
                height: '25px',
              }}
            >
              <ThermostatIcon />
            </Avatar>
          }
          action={
            <div
              style={{
                margin: '0px',
                padding: '0px',
                display: 'flex',
              }}
            >
              {smartPowerOutlet?._id ? (
                <SmartPowerOutletLink id={smartPowerOutlet?._id} />
              ) : null}
              {pairedSensor ? (
                <div
                  className="MuiButtonGroup-root MuiButtonGroup-text"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                    minWidth: '34px',
                  }}
                >
                  {device.isPairingActive ? (
                    <LinkIcon style={{ margin: '0px', height: '42px' }} />
                  ) : (
                    <LinkOffIcon
                      color={hasAlerts ? 'error' : 'warning'}
                      style={{
                        margin: '0px',
                        height: '42px',
                      }}
                    />
                  )}
                </div>
              ) : null}
              {canAccessPlm && isPlmActive ? (
                <div
                  className="MuiButtonGroup-root MuiButtonGroup-text"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                    minWidth: '40px',
                  }}
                >
                  <Button
                    onClick={() => setPlmAbortDialogOpen(true)}
                    disabled={abortLoading}
                    style={{
                      color: '#1E3D1D',
                      minWidth: '40px',
                      padding: '0px 6px 0px 0px',
                      margin: '0px 0px 0px 4px',
                    }}
                    title="PLM Active - Click to cancel"
                  >
                    <Avatar
                      sx={{
                        minWidth: '0px',
                        color: 'inherit',
                        width: {
                          xs: '16px',
                          sm: '24px',
                          md: '24px',
                        },
                        height: {
                          xs: '16px',
                          sm: '24px',
                          md: '24px',
                        },
                        fontSize: {
                          xs: 'medium',
                          sm: 'large',
                          md: 'large',
                        },
                        backgroundColor: 'transparent',
                      }}
                    >
                      <BoltIcon />
                    </Avatar>
                  </Button>
                </div>
              ) : (
                <MenuButtons
                  viewMode={viewMode}
                  saveDisabled={requiresEditReload || limitDraftInvalid}
                  handleClick={handleMenuClick}
                  supportsScheduling={
                    (device as MyThermostat & { supportsScheduling?: boolean })
                      .supportsScheduling ?? true
                  }
                />
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '40px',
                }}
                className="MuiButtonGroup-root MuiButtonGroup-text"
              >
                <ToggleAlertDetailsButton
                  device={device}
                  showDivider={true}
                  expand={alertsExpanded}
                  buttonLabel="Show alerts"
                  onClick={(event) => {
                    event.preventDefault();
                    setAlertsExpanded(!alertsExpanded);
                  }}
                />
                <ExpandMore
                  expand={expanded}
                  onClick={handleExpandClick}
                  aria-expanded={expanded}
                  aria-label="show more"
                >
                  <ExpandMoreIcon />
                </ExpandMore>
              </div>
            </div>
          }
          title={
            <Typography
              variant="h4"
              sx={{ color: '#1E3D1D', marginLeft: '10px' }}
            >
              {originalValues.name}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid lightgray',
            px: 2,
            py: 1,
            backgroundColor: '#ebf7f6',
          }}
        />
        <CardContent
          sx={{
            px: 0,
            py: 0,
            '&.MuiCardContent-root:last-child': { pb: 0 },
            ...(expanded || alertsExpanded
              ? {
                  paddingBottom: '0px',
                  borderBottom: '1px solid lightgray',
                }
              : {}),
          }}
        >
          {requiresEditReload && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" onClick={reloadConfirmedValues}>
                  Reload confirmed values
                </Button>
              }
            >
              Thermostat capabilities, mode, permissions or limits changed.
              Reload confirmed values before saving; this discards your draft.
            </Alert>
          )}
          <DisplayScreen
            heatTargetDigits={nativeTargetDigits(
              confirmedDevice,
              'heatSetpoint',
              preferredUnits,
            )}
            coolTargetDigits={nativeTargetDigits(
              confirmedDevice,
              'coolSetpoint',
              preferredUnits,
            )}
            autoTargetDigits={
              preferredUnits === Fahrenheit
                ? nativeTargetDigits(
                    confirmedDevice,
                    'setpoint',
                    preferredUnits,
                  )
                : undefined
            }
            separatePower={separatePower}
            powerMode={displayValues?.powerMode}
            canEditHeat={canWriteSetpoint('heatSetpoint')}
            canEditCool={canWriteSetpoint('coolSetpoint')}
            setpoint={nativeTargetDisplayValue(
              confirmedDevice,
              'setpoint',
              targetDisplayValues?.setpoint,
              preferredUnits,
            )}
            autoTargetConfirmed={
              separatePower || originalValues.operatingMode === 'Auto'
            }
            supportsSingleSetpointAutoMode={
              targetDisplayValues?.supportsSingleSetpointAutoMode
            }
            canEdit={
              !requiresEditReload &&
              !powerTargetBlockReason &&
              can('update', 'Thermostat') &&
              (device.supportsSingleSetpointAutoMode !== true ||
                (newValues ?? originalValues).operatingMode !== 'Auto' ||
                device.deviceMetrics?.some(
                  (metric) =>
                    metric?.key === 'setpoint' &&
                    metric.value?.readOnly === false,
                ) === true)
            }
            preferredUnits={preferredUnits}
            temperature={originalValues.temperature as number}
            heatSetpoint={nativeTargetDisplayValue(
              confirmedDevice,
              'heatSetpoint',
              targetDisplayValues?.heatSetpoint,
              preferredUnits,
            )}
            coolSetpoint={nativeTargetDisplayValue(
              confirmedDevice,
              'coolSetpoint',
              targetDisplayValues?.coolSetpoint,
              preferredUnits,
            )}
            humidity={originalValues.humidity as number}
            supportsHumidityMonitoring={
              device.supportsHumidityMonitoring === true
            }
            systemType={originalValues.systemType as string}
            operatingMode={targetDisplayValues?.operatingMode as string}
            operatingState={displayValues?.operatingState as string}
            timestamp={
              Boolean(
                deviceOfflineTimestamp ??
                  sourceOfflineTimestamp ??
                  device.timestamp,
              )
                ? (device.timestamp as number)
                : undefined
            }
            viewMode={viewMode}
            handleTargetChange={changeTarget}
            handleChange={handleChange}
          />
          {powerSnapshot && isEditingValues && (
            <Alert
              severity={confirmedPower === 'Unknown' ? 'warning' : 'info'}
              icon={false}
              sx={{
                px: 1.25,
                py: 0,
                borderRadius: 0,
                ...(!powerPending &&
                !powerTargetBlockReason &&
                confirmedPower !== 'Unknown'
                  ? { bgcolor: 'transparent', color: 'text.secondary' }
                  : {}),
                '& .MuiAlert-message': { py: 0.5, fontSize: '0.75rem' },
              }}
            >
              Power: {confirmedPower}. Selected mode:{' '}
              {powerSnapshot.operatingMode?.value === 'Fan'
                ? 'Fan only'
                : (powerSnapshot.operatingMode?.value ?? 'Unknown')}
              .{powerPending ? ' Command pending confirmation.' : ''}
              {powerTargetBlockReason ? ` ${powerTargetBlockReason}` : ''}
            </Alert>
          )}
          <hr
            style={{
              padding: '0px',
              margin: '0px 0px 0px 0px',
              border: '0.5px solid lightgray',
            }}
          />
          {(!requiresEditReload &&
            (viewMode === ViewMode.EDIT ||
              viewMode === ViewMode.EDIT_UNCHANGED) && (
              <SystemEdit
                canEditPowerMode={
                  powerSnapshot?.canEditPowerMode === true && !powerPending
                }
                canEditOperatingMode={
                  canWriteSetpoint('operatingMode') &&
                  (!powerSnapshot ||
                    (powerSnapshot.canEditOperatingMode && !powerPending))
                }
                canEditFanSpeed={
                  powerSnapshot?.canEditFanSpeed === true &&
                  !powerPending &&
                  canWriteSetpoint('fanSpeed')
                }
                canEditFanMode={!separatePower && canWriteSetpoint('fanMode')}
                thermostat={device}
                currentValues={newValues ? newValues : originalValues}
                handleUseCustomSetpointLimitsChange={
                  handleUseCustomSetpointLimitsChange
                }
                handleChange={handleChange}
                handleSetpointLimitChange={handleSetpointLimitChange}
                onLimitDraftValidityChange={setLimitDraftInvalid}
                handleSetpointLimitsChange={(limits) => {
                  const changed = {
                    ...newValues,
                    ...limits,
                  } as CurrentAttributes;
                  const conformed = conformToSetpointLimits(changed);
                  if (conformed === null) return false;
                  handleChange({ ...limits, ...conformed });
                  return true;
                }}
                targetUnits={preferredUnits}
              />
            )) ||
            (viewMode === ViewMode.NORMAL && (
              <StatusIcons
                preferredUnits={preferredUnits}
                thermostat={confirmedDevice}
                observationNow={observationNow}
                statusMessage={statusMessage}
                showStatusMessage={showStatusMessage}
                showResumeSchedule={showResumeSchedule()}
                briefScheduleStatus={briefScheduleStatus()}
                supportsScheduling={
                  (device as MyThermostat & { supportsScheduling?: boolean })
                    .supportsScheduling ?? true
                }
                values={{
                  CurrentAttributes: {
                    ...(newValues ? newValues : originalValues),
                    operatingMode: newValues?.operatingMode
                      ? (newValues.operatingMode as 'autox' | 'cooly' | 'heatz')
                      : originalValues?.operatingMode
                        ? (originalValues.operatingMode as
                            | 'auto'
                            | 'cool'
                            | 'heat')
                        : '',
                  },
                }}
              />
            )) ||
            ((viewMode === ViewMode.SCHEDULE ||
              viewMode === ViewMode.SCHEDULE_UNCHANGED) &&
              schedule && (
                <div style={{ paddingTop: '10px' }}>
                  <ThermostatSchedule
                    property={property as Partial<Property>}
                    unit={unit as Partial<Unit>}
                    thermostat={device}
                    schedule={newSchedule}
                    setViewMode={setViewMode}
                    scheduleType={scheduleType}
                    handleChange={handleScheduleChange}
                    handleClearSchedule={() => {
                      handleClearSchedule();
                    }}
                    handleTypeChange={handleScheduleTypeChange}
                    temperatureUnit={preferredUnits}
                    lastModified={schedule.timestamp}
                  />
                </div>
              )) ||
            (viewMode === ViewMode.UPDATING && (
              <UpdatePending
                status={'submitted'}
                deviceId={device.deviceId}
                getClearContext={() => updateAttempt.current}
                onCleared={handleUpdateStatusCleared}
              />
            )) ||
            (viewMode === ViewMode.AWAITING && (
              <UpdatePending
                status={'awaiting'}
                deviceId={device.deviceId}
                getClearContext={() => updateAttempt.current}
                onCleared={handleUpdateStatusCleared}
              />
            ))}
          {louvreSnapshot && (
            <LouvrePanel
              snapshot={louvreSnapshot}
              editing={
                !requiresEditReload &&
                (viewMode === ViewMode.EDIT ||
                  viewMode === ViewMode.EDIT_UNCHANGED)
              }
              value={(newValues ?? originalValues)?.airDirection}
              disabled={
                powerPending ||
                viewMode === ViewMode.UPDATING ||
                viewMode === ViewMode.AWAITING ||
                !can('update', 'Thermostat') ||
                !canWriteSetpoint('airDirection')
              }
              onChange={(airDirection) => handleChange({ airDirection })}
            />
          )}
          {permissionsSnapshot && !isEditingValues && (
            <LocalPermissionsPanel snapshot={permissionsSnapshot} />
          )}
        </CardContent>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <div
            className={
              usesObservationEvidence(confirmedDevice)
                ? 'thermostat-observation-details'
                : undefined
            }
          >
            <DeviceInfoBox infoEntries={deviceInfo} />
          </div>
        </Collapse>
        <DeviceAlerts device={device} alertsExpanded={alertsExpanded} />
        <ThermostatScheduleUpdateDialog
          thermostat={device}
          setpointLimits={{
            maxCoolSetpointLimit:
              updateThermostatVariables?.updates?.maxCoolSetpointLimit ??
              convertLimitTemperature(
                newValues?.maxCoolSetpointLimit,
                Celsius,
                preferredUnits,
              ),
            minCoolSetpointLimit:
              updateThermostatVariables?.updates?.minCoolSetpointLimit ??
              convertLimitTemperature(
                newValues?.minCoolSetpointLimit,
                Celsius,
                preferredUnits,
              ),
            maxHeatSetpointLimit:
              updateThermostatVariables?.updates?.maxHeatSetpointLimit ??
              convertLimitTemperature(
                newValues?.maxHeatSetpointLimit,
                Celsius,
                preferredUnits,
              ),
            minHeatSetpointLimit:
              updateThermostatVariables?.updates?.minHeatSetpointLimit ??
              convertLimitTemperature(
                newValues?.minHeatSetpointLimit,
                Celsius,
                preferredUnits,
              ),
          }}
          open={openThermostatScheduleUpdateDialog}
          handleCloseThermostatScheduleUpdateDialog={() =>
            setOpenThermostatScheduleUpdateDialog(false)
          }
          handleUpdateThermostat={(autoAdjust: boolean) =>
            handleUpdateThermostat(updateThermostatVariables, autoAdjust)
          }
          handleSetViewModeToNormal={() => setViewMode(ViewMode.NORMAL)}
        />
        {canAccessPlm && (
          <>
            <PlmAbortDialog
              open={plmAbortDialogOpen}
              onClose={() => setPlmAbortDialogOpen(false)}
              onAbortCurrent={handlePlmAbortCurrent}
              onAbortAll={handlePlmAbortAll}
              onViewSchedule={() => setPlmScheduleDialogOpen(true)}
              deviceName={originalValues?.name || 'Unknown Device'}
            />
            <PlmScheduleTimelineDialog
              open={plmScheduleDialogOpen}
              onClose={() => setPlmScheduleDialogOpen(false)}
              deviceName={originalValues?.name || 'Unknown Device'}
              tasks={plmScheduleTasks}
              eventResponseStart={
                plmEventResponseData?.plmEventResponseById?.scheduledStart
              }
              eventResponseEnd={
                plmEventResponseData?.plmEventResponseById?.scheduledEnd
              }
            />
          </>
        )}
      </Card>
    )
  ) : (
    <Card className="thermostat-loading-container">
      <CardContent className="thermostat-loading-2">
        <CircularProgress />
      </CardContent>
    </Card>
  );
}
